import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import {
  Box,
  Button,
  CircularProgress,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import RadioButtonCheckedIcon from "@mui/icons-material/RadioButtonChecked";
import {
  GoogleMap,
  Marker,
  OverlayView,
  Polyline,
  useLoadScript,
} from "@react-google-maps/api";
import { useNavigate, useLocation } from "react-router-dom";
import {
  getStopByCoordinates,
  getVehicleDirection,
  fetchInitialLocations,
} from "./mobileApis";
import backArrow from "../../../images/mobileStop/arrow_down.svg";
import Info_Banner from "../../../images/mobileStop/info_banner.svg";

import config from "../../../config.json";
import useSSE from "../../../hooks/useSSE";
import useBusRefresh from "./useBusRefresh";
import { appendMovementPoint } from "./etaUtils";
import {
  CHARGING_STATION_COORDS,
  CHARGING_STATION_RADIUS_METERS,
  haversineKm,
  isInactiveOrganization,
  nearestIndex,
  segmentDistance,
} from "./mobileScreenUtils";
import "./BusList.css";
import { orderedCoordinates, routeAllowsJourney } from "./journeyUtils";
import { useTranslation } from "react-i18next";
import { useSettings } from "../../../context/SettingsContext";
import { useMapIcons } from "../../../hooks/useMapIcons.js";
import {
  DirectionsCache,
  extractPathPoints,
  isValidDirectionsResult,
} from "../../dashboard/mapConstants.js";

/* --------------------------------------------------------------
   CONSTANTS
   -------------------------------------------------------------- */
const DIRECTION_REFRESH_INTERVAL = 30000;
const ARRIVING_DISTANCE_METERS = 500;
const ARRIVED_DISTANCE_METERS = 70;
const ARRIVED_TIME_SECONDS = 10;
const KEEP_ARRIVED_VISIBLE_MS = 3000;
const PROGRESS_AT_STOP_METERS = ARRIVED_DISTANCE_METERS;

const getStraightMetersToStop = (live, stop) => {
  const km = haversineKm(
    { lat: live?.latitude, lng: live?.longitude },
    stop,
  );
  if (km == null) return Infinity;
  return km * 1000;
};

const ETA_CONSTANTS = {
  AVG_SPEED: 20,
  TRAFFIC_MULTIPLIER: 1.1,
  MIN_STOP_TIME: 1,
  MAX_STOP_TIME: 35,
  HISTORY_POINTS: 15,
  MIN_TIME_DIFF: 10,
  MAX_SPEED_THRESHOLD: 100,
  MIN_DISTANCE_THRESHOLD: 10,
  SPEED_UPDATE_INTERVAL: 300000,
  EMA_WEIGHT: 0.8,
};
const EMPTY_ROUTES = [];

const GOOGLE_MAP_LIBRARIES = ["geometry"];
const DEFAULT_MAP_CENTER = { lat: 12.9716, lng: 77.5946 };
const LIVE_FLUSH_MS = 400;
const MAP_RESIZE_DEBOUNCE_MS = 150;

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const toLatLng = (point) => {
  const lat = toNumber(point?.lat ?? point?.latitude);
  const lng = toNumber(point?.lng ?? point?.longitude);
  if (lat === null || lng === null) return null;
  return { lat, lng };
};

const areCoordinatesClose = (a, b) => {
  const aPoint = toLatLng(a);
  const bPoint = toLatLng(b);
  if (!aPoint || !bPoint) return false;
  return (
    Math.abs(aPoint.lat - bPoint.lat) < 0.0001 &&
    Math.abs(aPoint.lng - bPoint.lng) < 0.0001
  );
};

const sortRouteCoordinates = (route) =>
  Array.isArray(route?.routeCoordinates)
    ? route.routeCoordinates
        .map((coord, index) => ({ ...coord, fallbackOrder: index }))
        .sort(
          (a, b) =>
            (toNumber(a.order) ?? a.fallbackOrder) -
            (toNumber(b.order) ?? b.fallbackOrder),
        )
    : [];

const findRouteCoordinateIndex = (coordinates, stop) =>
  coordinates.findIndex((coord) => areCoordinatesClose(coord, stop));

const STOP_SNAP_KM = 0.05;

const findStopIndexOnRoute = (coordinates, stop) => {
  const exactIdx = findRouteCoordinateIndex(coordinates, stop);
  if (exactIdx !== -1) return exactIdx;

  const point = toLatLng(stop);
  if (!point) return -1;

  const nearestIdx = nearestIndex(point, coordinates);
  if (nearestIdx === -1) return -1;

  const distanceKm = haversineKm(point, coordinates[nearestIdx]);
  return distanceKm != null && distanceKm <= STOP_SNAP_KM ? nearestIdx : -1;
};

const LOOP_CLOSURE_KM = 0.15;

const isLoopRoute = (route, coordinates = []) => {
  const type = String(route?.routeType || "").toUpperCase();
  if (type === "BI_DIRECTIONAL") return true;
  if (coordinates.length < 4) return false;
  const closureKm = haversineKm(
    coordinates[0],
    coordinates[coordinates.length - 1],
  );
  return closureKm != null && closureKm <= LOOP_CLOSURE_KM;
};

const joinLoopSegments = (first, second) =>
  first.length &&
  second.length &&
  areCoordinatesClose(first[first.length - 1], second[0])
    ? [...first, ...second.slice(1)]
    : [...first, ...second];

// `travelDirection` is the way the vehicle actually moves along the stored
// coordinate order. It only matters on a loop, where a bus whose destination
// sits behind it has to keep going forward and wrap around rather than
// retrace the road it just drove.
const getDirectedCoordinatesBetweenStops = (
  route,
  origin,
  destination,
  travelDirection,
) => {
  const coordinates = sortRouteCoordinates(route);
  if (coordinates.length < 2) return [];

  const originIdx = findStopIndexOnRoute(coordinates, origin);
  const destIdx = findStopIndexOnRoute(coordinates, destination);
  if (originIdx === -1 || destIdx === -1 || originIdx === destIdx) return [];

  if (isLoopRoute(route, coordinates)) {
    if (travelDirection === "source_to_dest" && destIdx < originIdx) {
      return joinLoopSegments(
        coordinates.slice(originIdx),
        coordinates.slice(0, destIdx + 1),
      );
    }

    if (travelDirection === "dest_to_source" && destIdx > originIdx) {
      return joinLoopSegments(
        [...coordinates.slice(0, originIdx + 1)].reverse(),
        [...coordinates.slice(destIdx)].reverse(),
      );
    }
  }

  return originIdx < destIdx
    ? coordinates.slice(originIdx, destIdx + 1)
    : [...coordinates.slice(destIdx, originIdx + 1)].reverse();
};

const sumPathDistanceKm = (coordinates) => {
  if (!Array.isArray(coordinates) || coordinates.length < 2) return null;
  let total = 0;
  for (let index = 0; index < coordinates.length - 1; index += 1) {
    const distance = haversineKm(coordinates[index], coordinates[index + 1]);
    if (distance == null) return null;
    total += distance;
  }
  return total;
};

const getWaypointFingerprint = (coordinates = []) => {
  const middle = coordinates.slice(1, -1);
  if (!middle.length) return "direct";
  const sample = [
    middle[0],
    middle[Math.floor(middle.length / 2)],
    middle[middle.length - 1],
  ]
    .map((coord) => {
      const point = toLatLng(coord);
      return point ? `${point.lat.toFixed(4)},${point.lng.toFixed(4)}` : "";
    })
    .filter(Boolean)
    .join("|");
  return `${middle.length}:${sample}`;
};

const buildRoutePathBetweenStops = (route, origin, destination) => {
  const coordinates = getDirectedCoordinatesBetweenStops(
    route,
    origin,
    destination,
  )
    .map(toLatLng)
    .filter(Boolean);
  if (!coordinates.length) return [];

  return clipPathToStops(coordinates, origin, destination);
};

const buildRouteCoordinatesBetweenStops = (route, origin, destination) =>
  getDirectedCoordinatesBetweenStops(route, origin, destination);

const isRouteForStops = (route, origin, destination) => {
  const coordinates = sortRouteCoordinates(route);
  return (
    findStopIndexOnRoute(coordinates, origin) !== -1 &&
    findStopIndexOnRoute(coordinates, destination) !== -1
  );
};

const isSameStop = (s, t) => {
  if (!s || !t) return false;
  if (s.id && t.id) return String(s.id) === String(t.id);
  if (s.name && t.name && s.name === t.name) return true;
  return areCoordinatesClose(s, t);
};

const getTravelOrderedStops = (stops, origin, destination) => {
  if (!Array.isArray(stops) || !origin || !destination) return [];
  const originIdx = stops.findIndex((stop) => isSameStop(stop, origin));
  const destIdx = stops.findIndex((stop) => isSameStop(stop, destination));
  if (originIdx === -1 || destIdx === -1) return [];
  if (originIdx === destIdx) return [stops[originIdx]];
  return originIdx < destIdx
    ? stops.slice(originIdx, destIdx + 1)
    : [...stops.slice(destIdx, originIdx + 1)].reverse();
};

const toProgressVehicleSnapshot = (bus) =>
  bus?.deviceId
    ? {
        deviceId: bus.deviceId,
        vehicleName: bus.vehicleName,
        routeId: bus.routeId,
        routeCoords: bus.routeCoords,
        journeyDirection: bus.journeyDirection,
        live: bus.live,
      }
    : null;

const pathBetweenStops = (routeCoords, fromStop, toStop) => {
  if (Array.isArray(routeCoords) && routeCoords.length >= 2) {
    const directed = getDirectedCoordinatesBetweenStops(
      { routeCoordinates: routeCoords },
      fromStop,
      toStop,
    )
      .map(toLatLng)
      .filter(Boolean);
    if (directed.length >= 2) return directed;
  }

  const from = toLatLng(fromStop);
  const to = toLatLng(toStop);
  return from && to ? [from, to] : [];
};

const projectOntoSegment = (start, end, point) => {
  const dx = end.lng - start.lng;
  const dy = end.lat - start.lat;
  const lengthSquared = dx * dx + dy * dy;
  if (lengthSquared === 0) return 0;
  const t =
    ((point.lng - start.lng) * dx + (point.lat - start.lat) * dy) /
    lengthSquared;
  return Math.min(1, Math.max(0, t));
};

const progressAlongPath = (path, vehiclePoint) => {
  if (!Array.isArray(path) || path.length < 2) return null;

  const lengths = [];
  let total = 0;
  for (let index = 0; index < path.length - 1; index += 1) {
    const distance = haversineKm(path[index], path[index + 1]) || 0;
    lengths.push(distance);
    total += distance;
  }
  if (total <= 0) return null;

  let bestDistance = Infinity;
  let bestAlong = 0;
  let walked = 0;
  lengths.forEach((segmentLength, index) => {
    const start = path[index];
    const end = path[index + 1];
    const t = projectOntoSegment(start, end, vehiclePoint);
    const projected = {
      lat: start.lat + (end.lat - start.lat) * t,
      lng: start.lng + (end.lng - start.lng) * t,
    };
    const distance = haversineKm(projected, vehiclePoint);
    if (distance != null && distance < bestDistance) {
      bestDistance = distance;
      bestAlong = walked + segmentLength * t;
    }
    walked += segmentLength;
  });

  if (!Number.isFinite(bestDistance)) return null;
  return {
    fraction: Math.min(1, Math.max(0, bestAlong / total)),
    distanceKm: bestDistance,
  };
};

const getVehicleStopProgress = ({
  live,
  routeCoords,
  travelStops,
  journeyDirection,
  vehicleDirection,
}) => {
  if (!travelStops.length || !routeCoords?.length) return null;
  if (!live?.latitude || !live?.longitude) return null;
  if (
    vehicleDirection &&
    vehicleDirection !== "unknown" &&
    journeyDirection &&
    vehicleDirection !== journeyDirection
  ) {
    return null;
  }

  const vehiclePoint = { lat: live.latitude, lng: live.longitude };
  const nearestIdx = nearestIndex(vehiclePoint, routeCoords);
  if (nearestIdx === -1) return null;

  let atStopIndex = -1;
  travelStops.forEach((stop, index) => {
    const distanceKm = haversineKm(vehiclePoint, stop);
    if (distanceKm != null && distanceKm * 1000 <= PROGRESS_AT_STOP_METERS) {
      atStopIndex = index;
    }
  });

  if (atStopIndex !== -1) {
    const nextIndex =
      atStopIndex + 1 < travelStops.length ? atStopIndex + 1 : -1;
    return {
      lastReached: atStopIndex,
      atStopIndex,
      nextIndex,
      segmentFromIndex: atStopIndex,
      segmentFraction: 0,
      travelStops,
    };
  }

  const fractionTowardNext = (fromStop, toStop, fromIdx, toIdx) => {
    if (fromIdx >= 0 && toIdx >= 0 && fromIdx !== toIdx) {
      const span = Math.abs(toIdx - fromIdx);
      const moved = fromIdx < toIdx ? nearestIdx - fromIdx : fromIdx - nearestIdx;
      return Math.min(1, Math.max(0, moved / span));
    }
    const totalKm = haversineKm(fromStop, toStop);
    const remainingKm = haversineKm(vehiclePoint, toStop);
    if (!totalKm || totalKm <= 0 || remainingKm == null) return 0;
    return Math.min(1, Math.max(0, 1 - remainingKm / totalKm));
  };

  let bestSegment = null;
  for (let index = 0; index < travelStops.length - 1; index += 1) {
    const fromIdx = findStopIndexOnRoute(routeCoords, travelStops[index]);
    const toIdx = findStopIndexOnRoute(routeCoords, travelStops[index + 1]);
    const containsVehicle =
      fromIdx >= 0 &&
      toIdx >= 0 &&
      (fromIdx < toIdx
        ? nearestIdx >= fromIdx && nearestIdx <= toIdx
        : nearestIdx <= fromIdx && nearestIdx >= toIdx);
    const along = progressAlongPath(
      pathBetweenStops(routeCoords, travelStops[index], travelStops[index + 1]),
      vehiclePoint,
    );
    const fraction = Math.max(
      along && along.distanceKm <= 0.4 ? along.fraction : 0,
      containsVehicle ? fractionTowardNext(travelStops[index], travelStops[index + 1], fromIdx, toIdx) : 0,
    );
    if (!containsVehicle && !(along && along.distanceKm <= 0.4)) continue;
    if (
      !bestSegment ||
      (containsVehicle && !bestSegment.containsVehicle) ||
      (containsVehicle === bestSegment.containsVehicle &&
        (along?.distanceKm ?? Infinity) < bestSegment.distanceKm)
    ) {
      bestSegment = {
        fromIndex: index,
        fraction,
        distanceKm: along?.distanceKm ?? Infinity,
        containsVehicle,
      };
    }
  }

  if (!bestSegment) return null;

  return {
    lastReached: bestSegment.fromIndex,
    atStopIndex: -1,
    nextIndex: bestSegment.fromIndex + 1,
    segmentFromIndex: bestSegment.fromIndex,
    segmentFraction: bestSegment.fraction,
    travelStops,
  };
};

const getConnectorFill = (stop, nextStop, progress) => {
  const empty = { fraction: 0, fromBottom: false };
  if (!progress?.travelStops?.length || !nextStop) return empty;

  const fromIdx = progress.travelStops.findIndex((item) => isSameStop(item, stop));
  const toIdx = progress.travelStops.findIndex((item) => isSameStop(item, nextStop));
  if (fromIdx === -1 || toIdx === -1 || Math.abs(fromIdx - toIdx) !== 1) {
    return empty;
  }

  const segmentStart = Math.min(fromIdx, toIdx);
  const segmentEnd = Math.max(fromIdx, toIdx);
  const fromBottom = fromIdx > toIdx;
  const reachedEnd =
    (progress.atStopIndex ?? -1) >= segmentEnd ||
    (progress.lastReached ?? -1) >= segmentEnd;
  if (reachedEnd || (progress.lastReached ?? -1) > segmentStart) {
    return { fraction: 1, fromBottom };
  }
  if ((progress.segmentFromIndex ?? -1) !== segmentStart) return empty;

  return {
    fraction: Math.min(1, Math.max(0, progress.segmentFraction || 0)),
    fromBottom,
  };
};

const getStopProgressState = (stop, progress) => {
  if (!progress?.travelStops?.length) return "upcoming";
  const idx = progress.travelStops.findIndex((item) => isSameStop(item, stop));
  if (idx === -1) return "upcoming";
  if (progress.atStopIndex === idx) return "current";
  if (progress.lastReached >= idx) return "passed";
  return "upcoming";
};

const isVehicleBetweenOriginAndDestination = (
  nearestIdx,
  originIdx,
  destIdx,
  journeyDirection,
) => {
  if (nearestIdx < 0 || originIdx < 0 || destIdx < 0 || originIdx === destIdx) {
    return false;
  }
  if (journeyDirection === "dest_to_source") {
    return nearestIdx < originIdx && nearestIdx >= destIdx;
  }
  return nearestIdx > originIdx && nearestIdx <= destIdx;
};

const inferDirectionAlongRoute = (history, routeCoords) => {
  if (!Array.isArray(history) || history.length < 2 || !routeCoords?.length) {
    return null;
  }

  let prevIdx = -1;
  let currIdx = -1;
  for (let index = history.length - 1; index >= 1; index -= 1) {
    currIdx = nearestIndex(history[index], routeCoords);
    prevIdx = nearestIndex(history[index - 1], routeCoords);
    if (prevIdx !== -1 && currIdx !== -1 && prevIdx !== currIdx) break;
  }

  if (prevIdx === -1 || currIdx === -1 || prevIdx === currIdx) return null;
  return currIdx > prevIdx ? "source_to_dest" : "dest_to_source";
};

const getEffectiveVehicleDirection = (
  declaredDirection,
  history,
  routeCoords,
) => {
  const inferred = inferDirectionAlongRoute(history, routeCoords);
  if (inferred) return inferred;
  if (
    declaredDirection === "source_to_dest" ||
    declaredDirection === "dest_to_source"
  ) {
    return declaredDirection;
  }
  return "unknown";
};

const ROUTE_MAP_ID = "a7388450e4f50eab65a0b0ff";

const MOBILE_MAP_OPTIONS = {
  mapId: ROUTE_MAP_ID,
  tilt: 0,
  streetViewControl: false,
  mapTypeControl: false,
  fullscreenControl: false,
  rotateControl: false,
  clickableIcons: false,
  gestureHandling: "greedy",
  disableDefaultUI: true,
  zoomControl: true,
};

const formatArrivalClock = (mins) => {
  const departure = new Date(Date.now() + mins * 60_000);
  return departure.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });
};

function MapStopLabel({ position, children }) {
  return (
    <OverlayView
      position={position}
      mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
    >
      <div style={{ position: "relative", width: 0, height: 0 }}>
        <div
          style={{
            position: "absolute",
            left: "50%",
            transform: "translateX(-50%)",
            bottom: "14px",
            backgroundColor: "white",
            color: "black",
            padding: "3px 8px",
            borderRadius: "4px",
            fontSize: "12px",
            fontWeight: 500,
            fontFamily: "Arial, sans-serif",
            boxShadow: "0 2px 4px rgba(0,0,0,0.25)",
            whiteSpace: "nowrap",
            zIndex: 1001,
            pointerEvents: "none",
          }}
        >
          {children}
        </div>
      </div>
    </OverlayView>
  );
}

function MapVehicleLabel({ position, color = "#008C23", onClick, children }) {
  return (
    <OverlayView
      position={position}
      mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
    >
      <div
        style={{
          position: "relative",
          top: "13px",
          left: "0px",
          width: "2px",
          height: "2px",
        }}
      >
        <svg
          style={{
            position: "absolute",
            left: "50%",
            transform: "translateX(-50%)",
            top: "0px",
            pointerEvents: "none",
            overflow: "visible",
            zIndex: 999,
          }}
          width="2"
          height="10"
        >
          <line
            x1="1"
            y1="0"
            x2="1"
            y2="10"
            stroke="black"
            strokeWidth="1.8"
            strokeDasharray="3 2"
          />
        </svg>
        <div
          onClick={onClick}
          style={{
            position: "absolute",
            top: "10px",
            left: "50%",
            transform: "translateX(-50%)",
            backgroundColor: color,
            color: "#fff",
            fontSize: "8px",
            fontWeight: 600,
            fontFamily: "Arial, sans-serif",
            lineHeight: "12px",
            padding: "6px",
            textAlign: "left",
            whiteSpace: "nowrap",
            borderRadius: "10px",
            zIndex: 1000,
            border: `1.5px solid ${color}`,
            cursor: onClick ? "pointer" : "default",
          }}
        >
          {children}
        </div>
      </div>
    </OverlayView>
  );
}

const getMapHeadingOriginToDestination = (origin, destination) => {
  const from = toLatLng(origin);
  const to = toLatLng(destination);
  if (!from || !to) return 0;
  const dy = to.lat - from.lat;
  const dx = to.lng - from.lng;
  let angle = Math.atan2(dy, dx) * (180 / Math.PI);
  angle = 90 - angle;
  return (angle + 360) % 360;
};

const getHeadingDifference = (current, target) => {
  const diff = Math.abs((current ?? 0) - (target ?? 0)) % 360;
  return Math.min(diff, 360 - diff);
};

const applyMapCamera = (map, { center, zoom, heading } = {}) => {
  if (!map) return;
  const camera = { tilt: 0 };
  if (center) camera.center = center;
  if (Number.isFinite(zoom)) camera.zoom = zoom;
  if (Number.isFinite(heading)) camera.heading = heading;

  if (typeof map.moveCamera === "function") {
    map.moveCamera(camera);
    return;
  }

  if (center) map.setCenter(center);
  if (Number.isFinite(zoom)) map.setZoom(zoom);
  if (Number.isFinite(heading) && typeof map.setHeading === "function") {
    map.setHeading(heading);
  }
  map.setOptions?.({
    ...(Number.isFinite(heading) ? { heading } : {}),
    tilt: 0,
  });
};

const getRotatedFitZoom = (origin, destination, width, height) => {
  const distanceKm = haversineKm(origin, destination);
  if (!distanceKm) return 14;

  const padX = Math.max(28, Math.round(width * 0.1));
  const padY = Math.max(56, Math.round(height * 0.2));
  const usableWidth = Math.max(80, width - padX * 2);
  const usableHeight = Math.max(80, height - padY * 2);
  const metersPerPixelAtZoom0 =
    156543.03392 * Math.cos(((origin.lat + destination.lat) / 2) * (Math.PI / 180));
  const distanceMeters = distanceKm * 1000;
  const zoomForHeight = Math.log2(
    (usableHeight * metersPerPixelAtZoom0) / distanceMeters,
  );
  const zoomForWidth = Math.log2(
    (usableWidth * metersPerPixelAtZoom0) / (distanceMeters * 0.4),
  );
  if (!Number.isFinite(zoomForHeight) || !Number.isFinite(zoomForWidth)) {
    return 14;
  }
  return Math.min(16.5, Math.max(11, Math.min(zoomForHeight, zoomForWidth)));
};

const getHeadingBetweenPoints = (from, to) => {
  if (!from || !to) return 0;
  const lat1 = (from.lat * Math.PI) / 180;
  const lat2 = (to.lat * Math.PI) / 180;
  const dLng = ((to.lng - from.lng) * Math.PI) / 180;
  const y = Math.sin(dLng) * Math.cos(lat2);
  const x =
    Math.cos(lat1) * Math.sin(lat2) -
    Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLng);
  return ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360;
};

const normalizeHeading = (deg) => ((deg % 360) + 360) % 360;

const getHeadingAlongPath = (position, path) => {
  if (!position || !Array.isArray(path) || path.length < 2) return null;

  let nearestIdx = 0;
  let nearestDist = Infinity;
  path.forEach((point, index) => {
    if (!point) return;
    const dLat = point.lat - position.lat;
    const dLng = point.lng - position.lng;
    const dist = dLat * dLat + dLng * dLng;
    if (dist < nearestDist) {
      nearestDist = dist;
      nearestIdx = index;
    }
  });

  const from = path[nearestIdx];
  let aheadIdx = nearestIdx;
  for (let i = nearestIdx + 1; i < path.length; i += 1) {
    const candidate = path[i];
    if (!candidate) continue;
    const dLat = candidate.lat - from.lat;
    const dLng = candidate.lng - from.lng;
    if (dLat * dLat + dLng * dLng > 0.00000004) {
      aheadIdx = i;
      break;
    }
  }

  if (aheadIdx === nearestIdx) {
    for (let i = nearestIdx - 1; i >= 0; i -= 1) {
      const candidate = path[i];
      if (!candidate) continue;
      const dLat = from.lat - candidate.lat;
      const dLng = from.lng - candidate.lng;
      if (dLat * dLat + dLng * dLng > 0.00000004) {
        return getHeadingBetweenPoints(candidate, from);
      }
    }
    return null;
  }

  return getHeadingBetweenPoints(from, path[aheadIdx]);
};

const createMobileRouteSegmentCacheKey = (
  route,
  origin,
  destination,
  coordinates,
) => {
  const routeId = route?.id ?? route?.routeId ?? "route";
  const originPoint = toLatLng(origin);
  const destinationPoint = toLatLng(destination);
  const directedCoords =
    coordinates ||
    getDirectedCoordinatesBetweenStops(route, origin, destination);

  return [
    "mobile_stop_directions_v2",
    routeId,
    origin?.id ?? origin?.name ?? "origin",
    originPoint?.lat?.toFixed(6),
    originPoint?.lng?.toFixed(6),
    destination?.id ?? destination?.name ?? "destination",
    destinationPoint?.lat?.toFixed(6),
    destinationPoint?.lng?.toFixed(6),
    getWaypointFingerprint(directedCoords),
  ].join("_");
};

const getJourneyDirectionForRoute = (route, origin, destination) => {
  if (!route || !origin || !destination) return null;
  const routeCoords = sortRouteCoordinates(route);
  const oIdx = findStopIndexOnRoute(routeCoords, origin);
  const dIdx = findStopIndexOnRoute(routeCoords, destination);
  if (oIdx === -1 || dIdx === -1) return null;
  return oIdx < dIdx ? "source_to_dest" : "dest_to_source";
};

const getTravelMinsFromPolyline = (route, origin, destination) => {
  const coordinates = getDirectedCoordinatesBetweenStops(
    route,
    origin,
    destination,
  );
  const distanceKm = sumPathDistanceKm(coordinates);
  if (distanceKm == null || distanceKm <= 0) return null;
  const mins =
    (distanceKm / ETA_CONSTANTS.AVG_SPEED) *
    60 *
    ETA_CONSTANTS.TRAFFIC_MULTIPLIER;
  return Math.max(1, Math.round(mins));
};

const nearestIndexOnPath = (path, point) => {
  let idx = 0;
  let min = Infinity;
  (path || []).forEach((candidate, index) => {
    if (!candidate) return;
    const dist =
      (candidate.lat - point.lat) ** 2 + (candidate.lng - point.lng) ** 2;
    if (dist < min) {
      min = dist;
      idx = index;
    }
  });
  return idx;
};

const clipPathToStops = (path, origin, destination) => {
  const originPoint = toLatLng(origin);
  const destinationPoint = toLatLng(destination);
  if (!originPoint || !destinationPoint) return path || [];
  if (!Array.isArray(path) || path.length < 2) {
    return [originPoint, destinationPoint];
  }

  const startIdx = nearestIndexOnPath(path, originPoint);
  const endIdx = nearestIndexOnPath(path, destinationPoint);
  if (startIdx === endIdx) {
    return [originPoint, destinationPoint];
  }

  const sliced =
    startIdx < endIdx
      ? path.slice(startIdx, endIdx + 1)
      : [...path.slice(endIdx, startIdx + 1)].reverse();

  return [originPoint, ...sliced.slice(1, -1), destinationPoint];
};

const extractRoadPathFromDirections = (directions) => {
  const stepPoints = extractPathPoints(directions);
  if (stepPoints.length > 1) return stepPoints;

  const overview = directions?.routes?.[0]?.overview_path || [];
  return overview
    .map((point) => ({
      lat: typeof point.lat === "function" ? point.lat() : point.lat,
      lng: typeof point.lng === "function" ? point.lng() : point.lng,
    }))
    .filter(
      (point) => Number.isFinite(point.lat) && Number.isFinite(point.lng),
    );
};

const createFullRouteCacheKey = (route) => {
  const coordinates = sortRouteCoordinates(route);
  const first = toLatLng(coordinates[0]);
  const last = toLatLng(coordinates[coordinates.length - 1]);

  return [
    "directions_mobile_full_route_v1",
    route?.id ?? route?.routeId ?? "route",
    coordinates.length,
    first?.lat?.toFixed(6),
    first?.lng?.toFixed(6),
    last?.lat?.toFixed(6),
    last?.lng?.toFixed(6),
    getWaypointFingerprint(coordinates),
  ].join("_");
};

// Mirrors the dashboard request exactly: every stored coordinate is sent in
// order and none are dropped, so Google returns the road the route was drawn
// along instead of choosing its own way between a sampled subset.
const buildFullRouteWaypoints = (coordinates) =>
  coordinates
    .slice(1, -1)
    .map((coord) => {
      const point = toLatLng(coord);
      if (!point) return null;
      return {
        location: new window.google.maps.LatLng(point.lat, point.lng),
        stopover: Boolean(coord.isStop),
      };
    })
    .filter(Boolean);

// Google splits a result into one leg per stopover waypoint, so the route's
// stop coordinates line up with the leg boundaries. Both ends of the route are
// boundaries whether or not they carry the isStop flag.
const getRouteLegBoundaries = (coordinates) =>
  coordinates.length < 2
    ? []
    : [
        coordinates[0],
        ...coordinates.slice(1, -1).filter((coord) => coord.isStop),
        coordinates[coordinates.length - 1],
      ];

const getLegPathPoints = (leg) =>
  (leg?.steps || [])
    .flatMap((step) => step?.path || [])
    .map((point) => ({
      lat: typeof point.lat === "function" ? point.lat() : point.lat,
      lng: typeof point.lng === "function" ? point.lng() : point.lng,
    }))
    .filter(
      (point) => Number.isFinite(point.lat) && Number.isFinite(point.lng),
    );

const secondsToMins = (seconds) =>
  seconds > 0 ? Math.max(1, Math.round(seconds / 60)) : null;

const sliceFullRouteByLegs = (coordinates, directions, origin, destination) => {
  const boundaries = getRouteLegBoundaries(coordinates);
  const legs = directions?.routes?.[0]?.legs || [];
  if (boundaries.length < 2 || legs.length !== boundaries.length - 1) return null;

  const originIdx = findStopIndexOnRoute(boundaries, origin);
  const destIdx = findStopIndexOnRoute(boundaries, destination);
  if (originIdx === -1 || destIdx === -1 || originIdx === destIdx) return null;

  const sliced = legs.slice(
    Math.min(originIdx, destIdx),
    Math.max(originIdx, destIdx),
  );
  const points = sliced.flatMap(getLegPathPoints);
  if (points.length < 2) return null;

  return {
    path: originIdx < destIdx ? points : points.reverse(),
    mins: secondsToMins(
      sliced.reduce((total, leg) => total + (leg?.duration?.value || 0), 0),
    ),
  };
};

// Matching has to move forward through the path: a route that doubles back
// passes the same coordinate twice, and the later pass must not win.
const anchorCoordinatesOnPath = (coordinates, path) => {
  let searchFrom = 0;

  return coordinates.map((coord) => {
    const point = toLatLng(coord);
    if (!point) return searchFrom;

    let bestIdx = searchFrom;
    let bestDist = Infinity;
    for (let idx = searchFrom; idx < path.length; idx += 1) {
      const dLat = path[idx].lat - point.lat;
      const dLng = path[idx].lng - point.lng;
      const dist = dLat * dLat + dLng * dLng;
      if (dist < bestDist) {
        bestDist = dist;
        bestIdx = idx;
      }
    }

    searchFrom = bestIdx;
    return bestIdx;
  });
};

// Steps are walked in the same order extractPathPoints concatenates them, so a
// step's position in this scan lines up with the path indices the anchors use.
const sumStepDurationSeconds = (directions, fromIdx, toIdx) => {
  let cursor = 0;
  let seconds = 0;

  (directions?.routes?.[0]?.legs || []).forEach((leg) => {
    (leg?.steps || []).forEach((step) => {
      const count = (step?.path || []).length;
      if (!count) return;

      const start = cursor;
      cursor += count;
      const overlap = Math.min(cursor, toIdx + 1) - Math.max(start, fromIdx);
      if (overlap > 0) {
        seconds += (step.duration?.value || 0) * (overlap / count);
      }
    });
  });

  return seconds;
};

// Used when the route carries no stop flags, so Google returned a single leg
// and the boundaries above cannot locate the two stops.
const sliceFullRouteByPath = (coordinates, directions, origin, destination) => {
  const stepPoints = extractPathPoints(directions);
  const path =
    stepPoints.length > 1
      ? stepPoints
      : extractRoadPathFromDirections(directions);
  if (path.length < 2) return null;

  const originIdx = findStopIndexOnRoute(coordinates, origin);
  const destIdx = findStopIndexOnRoute(coordinates, destination);
  if (originIdx === -1 || destIdx === -1 || originIdx === destIdx) return null;

  const anchors = anchorCoordinatesOnPath(coordinates, path);
  const startAnchor = anchors[originIdx];
  const endAnchor = anchors[destIdx];
  if (startAnchor === endAnchor) return null;

  const fromIdx = Math.min(startAnchor, endAnchor);
  const toIdx = Math.max(startAnchor, endAnchor);
  const slice = path.slice(fromIdx, toIdx + 1);
  if (slice.length < 2) return null;

  return {
    path: startAnchor < endAnchor ? slice : slice.reverse(),
    // Durations can only be apportioned when the indices came from the steps.
    mins:
      stepPoints.length > 1
        ? secondsToMins(sumStepDurationSeconds(directions, fromIdx, toIdx))
        : null,
  };
};

// The dashboard renders one Directions result for the whole route, so the only
// way this map can trace the same road is to reuse that result and cut the
// requested stretch out of it. The drive time is taken from the same cut so the
// two numbers cannot describe different journeys.
const buildSegmentFromFullRoute = (route, directions, origin, destination) => {
  const coordinates = sortRouteCoordinates(route);
  if (coordinates.length < 2) return null;

  return (
    sliceFullRouteByLegs(coordinates, directions, origin, destination) ||
    sliceFullRouteByPath(coordinates, directions, origin, destination)
  );
};

function MobileRouteMapPanel({
  currentOrigin,
  currentDestination,
  selectedRouteForDetails,
  routeMapPath,
  routeMapCoordinates,
  routeMapStops,
  vehiclesOnMap,
  getVehicleIcon,
  onSelectVehicle,
  onTravelTime,
  travelRoutes = [],
}) {
  const [routeMap, setRouteMap] = useState(null);
  const [routeDirections, setRouteDirections] = useState({});
  const [loadingRouteDirections, setLoadingRouteDirections] = useState(false);
  const [mapZoom, setMapZoom] = useState(13);
  const mapContainerRef = useRef(null);
  const lastFittedMapKeyRef = useRef(null);
  const userMovedMapRef = useRef(false);
  const iconCacheRef = useRef({});
  const resizeTimerRef = useRef(null);
  const hasInitialLayoutFitRef = useRef(false);

  const { isLoaded: isMapLoaded, loadError: mapLoadError } = useLoadScript({
    googleMapsApiKey: config.googleMapsApiKey,
    libraries: GOOGLE_MAP_LIBRARIES,
    preventGoogleFontsLoading: true,
  });

  useEffect(() => {
    iconCacheRef.current = {};
  }, [getVehicleIcon]);

  // The drawn line and every listed bus's drive time both come out of the
  // route's full Directions result, which is the request the dashboard already
  // makes, so the two screens cannot disagree about where a route goes.
  const measuredRoutes = useMemo(() => {
    const byId = new Map();
    [...travelRoutes, selectedRouteForDetails].forEach((route) => {
      const routeId = route?.id ?? route?.routeId;
      if (!route || routeId == null) return;
      if (!byId.has(String(routeId))) byId.set(String(routeId), route);
    });
    return [...byId.values()];
  }, [travelRoutes, selectedRouteForDetails]);

  useEffect(() => {
    if (!isMapLoaded || !measuredRoutes.length) {
      setLoadingRouteDirections(false);
      return undefined;
    }

    let cancelled = false;
    let pending = 0;
    const directionsService = new window.google.maps.DirectionsService();

    const store = (routeId, directions) =>
      setRouteDirections((prev) =>
        prev[routeId] === directions
          ? prev
          : { ...prev, [routeId]: directions },
      );

    measuredRoutes.forEach((route) => {
      const routeId = String(route.id ?? route.routeId);
      const coordinates = sortRouteCoordinates(route);
      if (coordinates.length < 2) return;

      const cacheKey = createFullRouteCacheKey(route);
      const cached = DirectionsCache.get(cacheKey);
      if (cached && isValidDirectionsResult(cached)) {
        store(routeId, cached);
        return;
      }

      const origin = toLatLng(coordinates[0]);
      const destination = toLatLng(coordinates[coordinates.length - 1]);
      if (!origin || !destination) return;

      pending += 1;
      directionsService.route(
        {
          origin: new window.google.maps.LatLng(origin.lat, origin.lng),
          destination: new window.google.maps.LatLng(
            destination.lat,
            destination.lng,
          ),
          waypoints: buildFullRouteWaypoints(coordinates),
          optimizeWaypoints: false,
          travelMode: window.google.maps.TravelMode.DRIVING,
        },
        (result, status) => {
          if (cancelled) return;
          pending -= 1;
          if (pending <= 0) setLoadingRouteDirections(false);
          if (status !== window.google.maps.DirectionsStatus.OK) return;
          DirectionsCache.set(cacheKey, result, route.routeColor || "#EE3B2B");
          store(routeId, result);
        },
      );
    });

    setLoadingRouteDirections(pending > 0);

    return () => {
      cancelled = true;
    };
  }, [isMapLoaded, measuredRoutes]);

  const routeSegment = useMemo(() => {
    const routeId = selectedRouteForDetails?.id ?? selectedRouteForDetails?.routeId;
    const directions = routeId == null ? null : routeDirections[String(routeId)];
    if (!directions || !currentOrigin || !currentDestination) return null;

    return buildSegmentFromFullRoute(
      selectedRouteForDetails,
      directions,
      currentOrigin,
      currentDestination,
    );
  }, [
    routeDirections,
    selectedRouteForDetails,
    currentOrigin,
    currentDestination,
  ]);

  const roadPath = routeSegment?.path || [];

  useEffect(() => {
    if (!currentOrigin || !currentDestination) return;

    measuredRoutes.forEach((route) => {
      const directions = routeDirections[String(route.id ?? route.routeId)];
      if (!directions) return;

      const segment = buildSegmentFromFullRoute(
        route,
        directions,
        currentOrigin,
        currentDestination,
      );
      if (segment?.mins == null) return;

      onTravelTime?.(
        createMobileRouteSegmentCacheKey(
          route,
          currentOrigin,
          currentDestination,
        ),
        segment.mins,
      );
    });
  }, [
    routeDirections,
    measuredRoutes,
    currentOrigin,
    currentDestination,
    onTravelTime,
  ]);

  const routeHeading = getMapHeadingOriginToDestination(
    currentOrigin,
    currentDestination,
  );

  const applyMapHeading = useCallback(
    (map, heading = routeHeading) => {
      if (!map || userMovedMapRef.current) return;
      applyMapCamera(map, { heading });
    },
    [routeHeading],
  );

  const layoutRouteMap = useCallback(
    (map, mapKey) => {
      if (!map || userMovedMapRef.current) return false;
      window.google?.maps?.event.trigger(map, "resize");

      const originPoint = toLatLng(currentOrigin);
      const destinationPoint = toLatLng(currentDestination);
      if (!originPoint || !destinationPoint) return false;

      const mapDiv = map.getDiv?.();
      const width =
        mapDiv?.clientWidth || mapContainerRef.current?.clientWidth || 0;
      const height =
        mapDiv?.clientHeight || mapContainerRef.current?.clientHeight || 0;
      if (width < 80 || height < 80) return false;

      applyMapCamera(map, {
        center: {
          lat: (originPoint.lat + destinationPoint.lat) / 2,
          lng: (originPoint.lng + destinationPoint.lng) / 2,
        },
        zoom: getRotatedFitZoom(originPoint, destinationPoint, width, height),
        heading: routeHeading,
      });
      lastFittedMapKeyRef.current = mapKey;
      return true;
    },
    [routeHeading, currentOrigin, currentDestination],
  );

  useEffect(() => {
    if (!isMapLoaded || !routeMap) return undefined;

    const mapKey = [
      selectedRouteForDetails?.id ?? selectedRouteForDetails?.routeId,
      currentOrigin?.id ?? currentOrigin?.name,
      currentDestination?.id ?? currentDestination?.name,
    ].join("_");

    if (lastFittedMapKeyRef.current !== mapKey) {
      userMovedMapRef.current = false;
    }
    if (userMovedMapRef.current) return undefined;
    if (lastFittedMapKeyRef.current === mapKey) return undefined;

    let frameOne = 0;
    let frameTwo = 0;

    const runFit = () => {
      if (!layoutRouteMap(routeMap, mapKey)) return;
    };

    frameOne = window.requestAnimationFrame(() => {
      frameTwo = window.requestAnimationFrame(runFit);
    });

    return () => {
      window.cancelAnimationFrame(frameOne);
      window.cancelAnimationFrame(frameTwo);
    };
  }, [
    isMapLoaded,
    routeMap,
    selectedRouteForDetails,
    currentOrigin,
    currentDestination,
    layoutRouteMap,
  ]);

  useEffect(() => {
    if (!routeMap || !mapContainerRef.current) return undefined;

    let headingAttempts = 0;
    const headingListener = routeMap.addListener("idle", () => {
      if (
        !lastFittedMapKeyRef.current ||
        userMovedMapRef.current ||
        headingAttempts > 12
      ) {
        return;
      }
      const currentHeading = routeMap.getHeading?.() ?? 0;
      if (getHeadingDifference(currentHeading, routeHeading) > 1) {
        headingAttempts += 1;
        applyMapHeading(routeMap, routeHeading);
      }
    });

    const triggerResize = () => {
      window.google?.maps?.event.trigger(routeMap, "resize");
      const height = mapContainerRef.current?.clientHeight || 0;
      if (userMovedMapRef.current || height < 160) return;

      const mapKey = [
        selectedRouteForDetails?.id ?? selectedRouteForDetails?.routeId,
        currentOrigin?.id ?? currentOrigin?.name,
        currentDestination?.id ?? currentDestination?.name,
      ].join("_");

      if (!hasInitialLayoutFitRef.current) {
        hasInitialLayoutFitRef.current = true;
        layoutRouteMap(routeMap, mapKey);
      }
    };

    const scheduleResize = () => {
      window.clearTimeout(resizeTimerRef.current);
      resizeTimerRef.current = window.setTimeout(
        triggerResize,
        MAP_RESIZE_DEBOUNCE_MS,
      );
    };

    scheduleResize();
    const observer = new ResizeObserver(scheduleResize);
    observer.observe(mapContainerRef.current);
    window.addEventListener("resize", scheduleResize);

    return () => {
      window.clearTimeout(resizeTimerRef.current);
      observer.disconnect();
      window.removeEventListener("resize", scheduleResize);
      window.google?.maps?.event.removeListener(headingListener);
    };
  }, [
    routeMap,
    selectedRouteForDetails,
    currentOrigin,
    currentDestination,
    layoutRouteMap,
    applyMapHeading,
    routeHeading,
  ]);

  // Google's result is already cut to the requested stretch, so clipping it
  // again by nearest point would only mangle a route that doubles back.
  const drawnRoutePath = roadPath.length > 1 ? roadPath : routeMapPath;
  const showIntermediateStopLabels = mapZoom >= 14;

  // Once the rider pans or zooms, the map must stop re-fitting and re-aligning
  // underneath them.
  const markUserMovedMap = useCallback(() => {
    userMovedMapRef.current = true;
  }, []);

  const mapOptions = useMemo(
    () => ({ ...MOBILE_MAP_OPTIONS, heading: routeHeading }),
    [routeHeading],
  );

  const getCachedVehicleIcon = (bus) => {
    if (!isMapLoaded || !window.google?.maps) return null;
    const pathHeading = getHeadingAlongPath(bus.position, drawnRoutePath);
    const geoHeading = Number.isFinite(pathHeading)
      ? pathHeading
      : bus.heading || 0;
    const screenHeading = normalizeHeading(geoHeading - routeHeading);
    const bucket = Math.round(screenHeading / 15) * 15;
    const cacheKey = `${bus.deviceId}_${bucket}_${bus.vehicleColor}`;
    if (!iconCacheRef.current[cacheKey]) {
      iconCacheRef.current[cacheKey] = getVehicleIcon(
        bus.deviceId,
        screenHeading,
        "source_to_dest",
        mapZoom || 14,
      );
    }
    return iconCacheRef.current[cacheKey];
  };

  if (mapLoadError) {
    return (
      <Typography textAlign="center" color="text.secondary" py={2}>
        Unable to load map.
      </Typography>
    );
  }

  if (!isMapLoaded) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
        <CircularProgress size={20} />
      </Box>
    );
  }

  if (routeMapCoordinates.length === 0 && routeMapStops.length === 0) {
    return (
      <Typography textAlign="center" color="text.secondary" py={2}>
        No route map available.
      </Typography>
    );
  }

  return (
    <Box
      ref={mapContainerRef}
      className="route-map-fill"
      onPointerDown={markUserMovedMap}
      onWheel={markUserMovedMap}
      onTouchStart={markUserMovedMap}
      onDoubleClick={markUserMovedMap}
    >
      <GoogleMap
        mapContainerStyle={{ width: "100%", height: "100%" }}
        defaultCenter={
          routeMapPath[0] || routeMapStops[0]?.position || DEFAULT_MAP_CENTER
        }
        defaultZoom={13}
        onLoad={(map) => {
          setRouteMap(map);
          setMapZoom(map.getZoom?.() || 13);
          map.addListener("dragstart", markUserMovedMap);
          map.addListener("zoom_changed", () => {
            const zoom = map.getZoom?.();
            if (Number.isFinite(zoom)) {
              setMapZoom((prev) => (prev === zoom ? prev : zoom));
            }
          });
        }}
        onUnmount={() => setRouteMap(null)}
        options={mapOptions}
      >
        {drawnRoutePath.length > 1 && (
          <Polyline
            path={drawnRoutePath}
            options={{
              strokeColor: selectedRouteForDetails?.routeColor || "#EE3B2B",
              strokeOpacity: 1,
              strokeWeight: 6,
            }}
          />
        )}

        {loadingRouteDirections && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              pointerEvents: "none",
            }}
          >
            <Box
              sx={{
                bgcolor: "rgba(255,255,255,0.9)",
                borderRadius: "999px",
                p: 1,
                boxShadow: "0 1px 6px rgba(0,0,0,0.18)",
              }}
            >
              <CircularProgress size={20} />
            </Box>
          </Box>
        )}

        {routeMapStops.map((stop, index) => {
          const isOrigin = areCoordinatesClose(stop, currentOrigin);
          const isDestination = areCoordinatesClose(stop, currentDestination);
          if (isOrigin || isDestination) return null;

          return (
            <React.Fragment key={`mid-stop-${stop.id || stop.name}-${index}`}>
              {showIntermediateStopLabels && stop.name && (
                <MapStopLabel position={stop.position}>{stop.name}</MapStopLabel>
              )}
              <Marker
                position={stop.position}
                zIndex={4}
                icon={{
                  path: window.google.maps.SymbolPath.CIRCLE,
                  scale: 6,
                  fillColor: "#20A8E0",
                  fillOpacity: 1,
                  strokeColor: "#FFFFFF",
                  strokeWeight: 2,
                }}
              />
            </React.Fragment>
          );
        })}

        {[
          {
            key: "origin",
            stop: currentOrigin,
            color: "#008C23",
            zIndex: 8,
          },
          {
            key: "destination",
            stop: currentDestination,
            color: "#EE3B2B",
            zIndex: 9,
          },
        ].map(({ key, stop, color, zIndex }) => {
          const position = toLatLng(stop);
          if (!position) return null;

          return (
            <React.Fragment key={key}>
              <MapStopLabel position={position}>
                {stop.name || (key === "origin" ? "Origin" : "Destination")}
              </MapStopLabel>
              <Marker
                position={position}
                zIndex={zIndex}
                icon={{
                  path: window.google.maps.SymbolPath.CIRCLE,
                  scale: 8,
                  fillColor: color,
                  fillOpacity: 1,
                  strokeColor: "#FFFFFF",
                  strokeWeight: 2,
                }}
              />
            </React.Fragment>
          );
        })}

        {vehiclesOnMap.map((bus) => (
          <React.Fragment key={`vehicle-${bus.deviceId}`}>
            <MapVehicleLabel
              position={bus.position}
              color={bus.vehicleColor}
              onClick={() => onSelectVehicle(bus.deviceId)}
            >
              {bus.vehicleName}
            </MapVehicleLabel>
            <Marker
              position={bus.position}
              zIndex={bus.isSelected ? 999 : 2}
              onClick={() => onSelectVehicle(bus.deviceId)}
              icon={getCachedVehicleIcon(bus) || undefined}
            />
          </React.Fragment>
        ))}
      </GoogleMap>
    </Box>
  );
}

/* --------------------------------------------------------------
   MAIN COMPONENT
   -------------------------------------------------------------- */
export default function BusList() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  const {
    originStop,
    destinationStop,
    organizationId,
    organization,
    routesWithVehicles: suppliedRoutes = EMPTY_ROUTES,
  } = location.state || {};

  const routesWithVehicles = useMemo(() => suppliedRoutes.map(route => ({
    ...route, routeCoordinates: orderedCoordinates(route),
  })), [suppliedRoutes]);

  // ---------- STATE ----------
  const [currentOrigin, setCurrentOrigin] = useState(originStop);
  const [currentDestination, setCurrentDestination] = useState(destinationStop);
  const [upcomingBuses, setUpcomingBuses] = useState([]);
  const [loadingBuses, setLoadingBuses] = useState(true);
  const [routeStops, setRouteStops] = useState([]);
  const [loadingStops, setLoadingStops] = useState(true);
  const [liveData, setLiveData] = useState({});
  const [isSendingSOS, setIsSendingSOS] = useState(false);
  const [sosSent, setSOSSent] = useState(false);
  const [activeRouteTab, setActiveRouteTab] = useState("stops");
  const [selectedBusDeviceId, setSelectedBusDeviceId] = useState(null);
  const [progressVehicle, setProgressVehicle] = useState(null);
  const [routeTravelTimes, setRouteTravelTimes] = useState({});
  const [sortMode, setSortMode] = useState("arrival");

  const { settings, fetchSettings } = useSettings();

  // ---------- REFS ----------
  const busesRef = useRef([]);
  const vehicleDirectionCache = useRef({});
  const directionIntervalRef = useRef(null);
  const vehicleStateRef = useRef({});
  const historicalAvgSpeeds = useRef({});
  const [, setDirections] = useState({});
  const [, setStatuses] = useState(null);

  const vehicleSpeedHistory = useRef({});
  const previousETAsRef = useRef({});
  const liveDataRef = useRef({});
  const liveFlushTimerRef = useRef(null);
  const lastStopProgressRef = useRef(null);
  const lastUpcomingSignatureRef = useRef("");

  const currentUser = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("userState") || "{}")?.user || {};
    } catch (error) {
      console.error("Failed to parse userState for SOS alert:", error);
      return {};
    }
  }, []);

  const resolvedOrganizationId =
    organizationId || organization?.id || currentUser?.organizationId || null;

  const selectedBus = useMemo(() => {
    if (!upcomingBuses.length || !selectedBusDeviceId) return null;
    return (
      upcomingBuses.find((bus) => bus.deviceId === selectedBusDeviceId) || null
    );
  }, [upcomingBuses, selectedBusDeviceId]);

  const pinProgressVehicle = useCallback((bus) => {
    const snapshot = toProgressVehicleSnapshot(bus);
    if (!snapshot) return;
    setProgressVehicle((prev) => {
      if (prev?.deviceId && prev.deviceId !== snapshot.deviceId) {
        lastStopProgressRef.current = null;
      }
      if (
        prev &&
        prev.deviceId === snapshot.deviceId &&
        prev.vehicleName === snapshot.vehicleName &&
        String(prev.routeId) === String(snapshot.routeId) &&
        prev.journeyDirection === snapshot.journeyDirection
      ) {
        return {
          ...prev,
          routeCoords: snapshot.routeCoords || prev.routeCoords,
          live: snapshot.live || prev.live,
        };
      }
      return snapshot;
    });
  }, []);

  const routeForProgress = useMemo(() => {
    const routeId = progressVehicle?.routeId ?? selectedBus?.routeId;
    if (routeId == null) return null;
    return (
      routesWithVehicles.find(
        (route) => String(route.id ?? route.routeId) === String(routeId),
      ) || null
    );
  }, [routesWithVehicles, progressVehicle, selectedBus]);

  const selectedRouteForDetails = useMemo(() => {
    const busRoute = routeForProgress;

    // The map traces the stretch between the two chosen stops, so a bus whose
    // route does not pass both of them cannot decide what gets drawn.
    if (busRoute && isRouteForStops(busRoute, currentOrigin, currentDestination)) {
      return busRoute;
    }

    const matchingRoute = routesWithVehicles.find((route) =>
      isRouteForStops(route, currentOrigin, currentDestination),
    );

    return matchingRoute || busRoute || routesWithVehicles[0] || null;
  }, [routesWithVehicles, routeForProgress, currentOrigin, currentDestination]);

  const mapIconVehicles = useMemo(
    () =>
      routesWithVehicles.flatMap((route) =>
        (route.vehicles || []).map((vehicle) => {
          const deviceId =
            vehicle.deviceId ||
            vehicle.device_id ||
            vehicle.device?.id ||
            vehicle.id;
          return {
            ...vehicle,
            device_id: deviceId,
            vehicle_color:
              vehicle.vehicle_color ||
              vehicle.color ||
              vehicle.vehicleColor ||
              "green",
          };
        }),
      ),
    [routesWithVehicles],
  );

  const mapIconRoutes = useMemo(
    () =>
      routesWithVehicles.map((route) => ({
        id: route.id ?? route.routeId,
        name: route.name,
        routeColor: route.routeColor,
      })),
    [routesWithVehicles],
  );

  const { getVehicleIcon } = useMapIcons(
    settings,
    mapIconVehicles,
    [],
    mapIconRoutes,
  );

  useEffect(() => {
    if (organizationId && fetchSettings && !settings?.vehicleIcons) {
      fetchSettings(organizationId);
    }
  }, [organizationId, fetchSettings, settings?.vehicleIcons]);

  const routeMapPath = useMemo(
    () =>
      buildRoutePathBetweenStops(
        selectedRouteForDetails,
        currentOrigin,
        currentDestination,
      ),
    [selectedRouteForDetails, currentOrigin, currentDestination],
  );

  const journeyStops = useMemo(() => {
    if (!currentOrigin || !currentDestination || !routeStops.length) {
      return routeStops;
    }

    const same = (s, t) => {
      if (s.id && t.id) return s.id === t.id;
      return (
        s.name === t.name ||
        (Math.abs(s.latitude - t.latitude) < 0.0001 &&
          Math.abs(s.longitude - t.longitude) < 0.0001)
      );
    };

    const originIdx = routeStops.findIndex((s) => same(s, currentOrigin));
    const destIdx = routeStops.findIndex((s) => same(s, currentDestination));
    if (originIdx === -1 || destIdx === -1) return routeStops;

    const start = Math.min(originIdx, destIdx);
    const end = Math.max(originIdx, destIdx);
    return routeStops.slice(start, end + 1);
  }, [routeStops, currentOrigin, currentDestination]);

  const routeMapStops = useMemo(
    () =>
      journeyStops
        .map((stop) => {
          const position = toLatLng(stop);
          return position ? { ...stop, position } : null;
        })
        .filter(Boolean),
    [journeyStops],
  );

  const routeMapCoordinates = useMemo(
    () =>
      buildRouteCoordinatesBetweenStops(
        selectedRouteForDetails,
        currentOrigin,
        currentDestination,
      ),
    [selectedRouteForDetails, currentOrigin, currentDestination],
  );

  const travelRoutes = useMemo(() => {
    if (!currentOrigin || !currentDestination) return [];
    return routesWithVehicles.filter((route) =>
      isRouteForStops(route, currentOrigin, currentDestination),
    );
  }, [routesWithVehicles, currentOrigin, currentDestination]);

  const getTravelTimeForRoute = useCallback(
    (route) => {
      if (!route || !currentOrigin || !currentDestination) return null;
      const cacheKey = createMobileRouteSegmentCacheKey(
        route,
        currentOrigin,
        currentDestination,
      );
      return routeTravelTimes[cacheKey] ?? null;
    },
    [routeTravelTimes, currentOrigin, currentDestination],
  );

  const displayedBuses = useMemo(() => {
    const list = upcomingBuses.map((bus) => {
      const route = routesWithVehicles.find(
        (item) => String(item.id ?? item.routeId) === String(bus.routeId),
      );
      const travelTimeMins =
        getTravelTimeForRoute(route) ??
        getTravelMinsFromPolyline(
          route || { routeCoordinates: bus.routeCoords },
          currentOrigin,
          currentDestination,
        );
      return {
        ...bus,
        travelTimeMins,
        sortTravelMins: travelTimeMins ?? Infinity,
      };
    });

    if (sortMode === "travel_asc") {
      return [...list].sort((a, b) => a.sortTravelMins - b.sortTravelMins);
    }
    if (sortMode === "travel_desc") {
      return [...list].sort((a, b) => b.sortTravelMins - a.sortTravelMins);
    }
    return [...list].sort((a, b) => a.sortMins - b.sortMins);
  }, [
    upcomingBuses,
    routesWithVehicles,
    getTravelTimeForRoute,
    sortMode,
    currentOrigin,
    currentDestination,
  ]);

  const vehiclesOnMap = useMemo(() => {
    const toMapVehicle = (bus, isTracked) => {
      const live = liveData[bus.deviceId] || bus.live;
      const lat = Number(live?.latitude);
      const lng = Number(live?.longitude);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;

      const reportedHeading = Number(live?.heading);
      const history = vehicleSpeedHistory.current[bus.deviceId] || [];
      const historyHeading =
        history.length >= 2
          ? getHeadingBetweenPoints(
              history[history.length - 2],
              history[history.length - 1],
            )
          : 0;

      const iconVehicle = mapIconVehicles.find(
        (vehicle) => String(vehicle.device_id) === String(bus.deviceId),
      );

      return {
        ...bus,
        position: { lat, lng },
        isSelected: isTracked,
        heading: Number.isFinite(reportedHeading)
          ? reportedHeading
          : historyHeading,
        direction: bus.direction || bus.journeyDirection || "unknown",
        vehicleColor: iconVehicle?.vehicle_color || "#008C23",
      };
    };

    const trackedId = progressVehicle?.deviceId || selectedBus?.deviceId;
    const mapped = displayedBuses
      .map((bus) => toMapVehicle(bus, bus.deviceId === trackedId))
      .filter(Boolean);

    if (
      progressVehicle?.deviceId &&
      !mapped.some((bus) => bus.deviceId === progressVehicle.deviceId)
    ) {
      const tracked = toMapVehicle(progressVehicle, true);
      if (tracked) mapped.push(tracked);
    }

    return mapped;
  }, [
    displayedBuses,
    liveData,
    selectedBus,
    progressVehicle,
    mapIconVehicles,
  ]);

  useEffect(() => {
    lastStopProgressRef.current = null;
    setProgressVehicle(null);
  }, [currentOrigin?.id, currentOrigin?.name, currentDestination?.id, currentDestination?.name]);

  useEffect(() => {
    if (!upcomingBuses.length) {
      setSelectedBusDeviceId(null);
      return;
    }

    const selectedBusStillVisible = upcomingBuses.some(
      (bus) => bus.deviceId === selectedBusDeviceId,
    );
    if (selectedBusStillVisible) return;

    if (
      progressVehicle?.deviceId &&
      selectedBusDeviceId === progressVehicle.deviceId
    ) {
      setSelectedBusDeviceId(null);
      return;
    }

    if (!selectedBusDeviceId && progressVehicle?.deviceId) return;

    setSelectedBusDeviceId(upcomingBuses[0].deviceId);
  }, [upcomingBuses, selectedBusDeviceId, progressVehicle?.deviceId]);

  const handleTravelTime = useCallback((cacheKey, mins) => {
    setRouteTravelTimes((prev) =>
      prev[cacheKey] === mins ? prev : { ...prev, [cacheKey]: mins },
    );
  }, []);

  useEffect(() => {
    previousETAsRef.current = {};
  }, [currentOrigin, currentDestination]);

  // ---------- DEVICE IDS ----------
  const deviceIds = useMemo(() => {
    const ids = [];
    routesWithVehicles.forEach((r) => {
      (r.vehicles || [])
        .filter((v) => v.status === "ACTIVE")
        .forEach((v) => {
          const id =
            v.deviceId || v.device_id || (v.device && v.device.id) || v.id;
          if (id && !ids.includes(id)) ids.push(id);
        });
    });
    return ids;
  }, [routesWithVehicles]);

  // ---------- SSE ----------
  const handleSSE = useCallback((msg) => {
    if (msg.type === "connected") return;
    const deviceId = msg.device_id ?? msg.vehicleId ?? msg.deviceId;
    const lat = Number(msg.Latitude ?? msg.latitude);
    const lng = Number(msg.Longitude ?? msg.longitude);
    const speed = Number(msg.speed ?? msg.Speed);
    const heading = Number(msg.heading ?? msg.Heading ?? msg.CourseAngle);

    if (!deviceId || !Number.isFinite(lat) || !Number.isFinite(lng)) return;

    const nextPoint = {
      latitude: isNaN(lat) ? null : lat,
      longitude: isNaN(lng) ? null : lng,
      speedKph: isNaN(speed) ? null : speed,
      heading: isNaN(heading)
        ? liveDataRef.current[deviceId]?.heading ?? null
        : heading,
      receivedAt: new Date().toISOString(),
    };

    liveDataRef.current = {
      ...liveDataRef.current,
      [deviceId]: nextPoint,
    };

    if (!liveFlushTimerRef.current) {
      liveFlushTimerRef.current = window.setTimeout(() => {
        liveFlushTimerRef.current = null;
        setLiveData({ ...liveDataRef.current });
      }, LIVE_FLUSH_MS);
    }

    if (!isNaN(lat) && !isNaN(lng)) {
      const point = {
        lat,
        lng,
        timestamp: Date.now(),
        reportedSpeed: speed ?? 0,
      };
      const history = vehicleSpeedHistory.current[deviceId] || [];
      vehicleSpeedHistory.current[deviceId] = [
        ...history.slice(-ETA_CONSTANTS.HISTORY_POINTS),
        point,
      ];
    }
  }, []);

  useEffect(
    () => () => {
      if (liveFlushTimerRef.current) {
        window.clearTimeout(liveFlushTimerRef.current);
      }
    },
    [],
  );

  useSSE(deviceIds, config.baseURL, handleSSE, { reconnect: true });

  // Poll both sources even when the streaming connection is quiet.
  useBusRefresh(deviceIds, setLiveData, setDirections, organizationId, setStatuses);

  // -----------------------------------------------------------------
  // --------------------- ROUTE TRAVEL TIMES -----------------------
  // -----------------------------------------------------------------
  useEffect(() => {
    if (!currentOrigin || !currentDestination || !travelRoutes.length) {
      setRouteTravelTimes({});
      return;
    }

    // A rough straight-line estimate so the cards show something immediately.
    // The map panel replaces it with the drive time of the stretch it draws.
    const updates = {};

    for (const route of travelRoutes) {
      const polyMins = getTravelMinsFromPolyline(
        route,
        currentOrigin,
        currentDestination,
      );
      if (polyMins == null) continue;

      updates[
        createMobileRouteSegmentCacheKey(route, currentOrigin, currentDestination)
      ] = polyMins;
    }

    if (Object.keys(updates).length) {
      setRouteTravelTimes((prev) => ({ ...prev, ...updates }));
    }
  }, [travelRoutes, currentOrigin, currentDestination]);

  // -----------------------------------------------------------------
  // --------------------- JOURNEY DIRECTION ------------------------
  // -----------------------------------------------------------------
  const getJourneyDirection = useCallback(() => {
    if (!currentOrigin || !currentDestination || !routesWithVehicles.length) {
      return null;
    }

    const route =
      selectedRouteForDetails ||
      routesWithVehicles.find((item) =>
        isRouteForStops(item, currentOrigin, currentDestination),
      ) ||
      routesWithVehicles[0];

    return getJourneyDirectionForRoute(route, currentOrigin, currentDestination);
  }, [
    currentOrigin,
    currentDestination,
    routesWithVehicles,
    selectedRouteForDetails,
  ]);

  // -----------------------------------------------------------------
  // --------------------- WARNING MESSAGE LOGIC --------------------
  // -----------------------------------------------------------------

  const normalizeStopName = (name) => {
    if (!name) return "";
    return name.trim().toLowerCase().replace(/\s+/g, " "); // Replace multiple spaces with single space
  };

  // Update constants to normalized versions
  const SPECIAL_ORIGINS = ["p3-parking"];
  const SPECIAL_DESTINATIONS = ["bial t1-departure"]; // Single space in normalized form
  const MANIPAL_HOSPITAL = "manipal hospital";

  // Update warningMessage useMemo:
  const warningMessage = useMemo(() => {
    const originName = normalizeStopName(currentOrigin?.name);
    const destName = normalizeStopName(currentDestination?.name);
    const journeyDir = getJourneyDirection();

    // Case 1: P3-Parking → BIAL T1-Departure
    if (
      SPECIAL_ORIGINS.includes(originName) &&
      SPECIAL_DESTINATIONS.includes(destName)
    ) {
      return "Shuttles might not be available from P3-Parking to BIAL T1-Departure. Please communicate with the driver.";
    }

    // Case 2: source_to_dest + Manipal Hospital
    if (journeyDir === "source_to_dest" && destName === MANIPAL_HOSPITAL) {
      return "Shuttles might not be available to Manipal Hospital. Please communicate with the driver.";
    }

    // Case 3: dest_to_source + BIAL T1-Departure
    if (
      journeyDir === "dest_to_source" &&
      SPECIAL_DESTINATIONS.includes(destName)
    ) {
      return "Shuttles cannot go to BIAL T1-Departure when the vehicle is returning to . Please communicate with the driver.";
    }

    return null;
  }, [currentOrigin, currentDestination, getJourneyDirection]);

  const shouldHideBuses = useMemo(() => {
    const originName = normalizeStopName(currentOrigin?.name);
    const destName = normalizeStopName(currentDestination?.name);
    if (originName === "p3-parking" && destName === "bial t1-departure") return true;
    // Apply the same route eligibility as the destination picker, including
    // its adjacent-stop exceptions.
    return !routesWithVehicles.some(route => routeAllowsJourney(route, currentOrigin, currentDestination));
  }, [currentOrigin, currentDestination, routesWithVehicles]);

  // -----------------------------------------------------------------
  // --------------------- FETCH STOP NAMES -------------------------
  // -----------------------------------------------------------------
  useEffect(() => {
    let mounted = true;
    async function fetchStops() {
      if (!routesWithVehicles.length || !organizationId) {
        if (mounted) {
          setRouteStops([]);
          setLoadingStops(false);
        }
        return;
      }
      const allCoordinates = sortRouteCoordinates(selectedRouteForDetails);
      const coordinates = allCoordinates.some((coord) => coord.isStop)
        ? allCoordinates.filter((coord) => coord.isStop)
        : allCoordinates;
      if (!coordinates.length) {
        if (mounted) {
          setRouteStops([]);
          setLoadingStops(false);
        }
        return;
      }
      if (mounted) setLoadingStops(true);

      try {
        const promises = coordinates.map(async (coord, idx) => {
          try {
            const stop = await getStopByCoordinates(
              coord.longitude,
              coord.latitude,
              organizationId,
            );
            if (stop?.name) {
              return {
                name: stop.name,
                latitude: coord.latitude,
                longitude: coord.longitude,
                originalIndex: idx,
                id: stop.id,
              };
            }
          } catch (_) {}
          return null;
        });

        const results = await Promise.all(promises);
        const filtered = results.filter(Boolean);
        filtered.sort((a, b) => a.originalIndex - b.originalIndex);

        const same = (s, t) => {
          if (s.id && t.id) return s.id === t.id;
          return (
            s.name === t.name ||
            (Math.abs(s.latitude - t.latitude) < 0.0001 &&
              Math.abs(s.longitude - t.longitude) < 0.0001)
          );
        };

        // -----------------------------------------------------------------
        // FILTER SPECIAL STOPS (GLOBAL FILTER)
        // -----------------------------------------------------------------
        let processedStops = filtered;
        if (currentOrigin && filtered.length > 0) {
          const oIdxBeforeFilter = filtered.findIndex((s) =>
            same(s, currentOrigin),
          );

          if (oIdxBeforeFilter !== -1) {
            processedStops = filtered.filter((stop, idx) => {
              const stopName = normalizeStopName(stop.name);
              const isManipal = stopName === "manipal hospital";
              const isBialDeparture = stopName === "bial t1-departure";

              if (!isManipal && !isBialDeparture) return true;

              // Found special stop in list
              const stopIdx = idx;
              let shouldShow = true;

              if (isManipal) {
                // Rule 1: Manipal restricted in source_to_dest (Origin < Stop)
                // Allowed if: Adjacent (Origin = Stop - 1) OR dest_to_source (Origin > Stop)
                const isSourceToDest = oIdxBeforeFilter < stopIdx;
                const isAdjacent = oIdxBeforeFilter === stopIdx - 1;

                if (isSourceToDest && !isAdjacent) {
                  shouldShow = false;
                }
              } else if (isBialDeparture) {
                // Rule 2: BIAL restricted in dest_to_source (Origin > Stop)
                // Allowed if: Adjacent (Origin = Stop + 1) OR source_to_dest (Origin < Stop)
                const isDestToSource = oIdxBeforeFilter > stopIdx;
                const isAdjacent = oIdxBeforeFilter === stopIdx + 1;

                if (isDestToSource && !isAdjacent) {
                  shouldShow = false;
                }
              }

              return shouldShow;
            });
          }
        }

        let stopsToShow = processedStops;
        let remainingStops = [];

        if (currentOrigin && currentDestination) {
          const oIdx = processedStops.findIndex((s) => same(s, currentOrigin));
          const dIdx = processedStops.findIndex((s) =>
            same(s, currentDestination),
          );
          if (oIdx !== -1 && dIdx !== -1) {
            if (oIdx < dIdx) {
              stopsToShow = processedStops.slice(oIdx, dIdx + 1);
              remainingStops = processedStops.slice(dIdx + 1);
            } else {
              stopsToShow = processedStops.slice(dIdx, oIdx + 1).reverse();
              remainingStops = processedStops.slice(0, dIdx).reverse();
            }
          }
        }

        let lastStopOriginalIndex;
        if (currentOrigin && currentDestination) {
          const oIdx = processedStops.findIndex((s) => same(s, currentOrigin));
          const dIdx = processedStops.findIndex((s) =>
            same(s, currentDestination),
          );
          if (oIdx !== -1 && dIdx !== -1) {
            lastStopOriginalIndex =
              oIdx < dIdx
                ? processedStops[processedStops.length - 1]?.originalIndex
                : processedStops[0]?.originalIndex;
          } else {
            lastStopOriginalIndex =
              processedStops[processedStops.length - 1]?.originalIndex;
          }
        } else {
          lastStopOriginalIndex =
            processedStops[processedStops.length - 1]?.originalIndex;
        }

        stopsToShow = stopsToShow.map((s) => {
          const labelParts = [];
          const isO = currentOrigin && same(s, currentOrigin);
          const isD = currentDestination && same(s, currentDestination);
          const isLast = s.originalIndex === lastStopOriginalIndex;

          if (isO)
            labelParts.push({ text: "Leaving from", className: "leaving" });
          if (isD) labelParts.push({ text: "Going To", className: "going" });
          if (isLast) labelParts.push({ text: "Last Stop", className: "last" });

          return { ...s, labelParts };
        });

        remainingStops = remainingStops.map((s) => {
          const isLast = s.originalIndex === lastStopOriginalIndex;
          return {
            ...s,
            labelParts: isLast
              ? [{ text: "Last Stop", className: "last" }]
              : [],
          };
        });

        const all = [...stopsToShow, ...remainingStops];
        if (mounted) setRouteStops(all);
      } catch (err) {
        console.error("Error fetching stops:", err);
        if (mounted) setRouteStops([]);
      } finally {
        if (mounted) setLoadingStops(false);
      }
    }

    fetchStops();
    return () => {
      mounted = false;
    };
  }, [
    routesWithVehicles,
    selectedRouteForDetails,
    organizationId,
    currentOrigin,
    currentDestination,
  ]);

  // Both SSE and polling feed the same rolling 15-point movement history.
  useEffect(() => {
    Object.entries(liveData).forEach(([deviceId, live]) => {
      vehicleSpeedHistory.current[deviceId] = appendMovementPoint(
        vehicleSpeedHistory.current[deviceId] || [], live);
    });
  }, [liveData]);

  // -----------------------------------------------------------------
  // --------------------- REFRESH DIRECTIONS EVERY 30s -------------
  // -----------------------------------------------------------------
  useEffect(() => {
    if (!deviceIds.length) return;

    const refresh = async () => {
      for (const id of deviceIds) {
        try {
          const dir = await getVehicleDirection(id);
          vehicleDirectionCache.current[id] = dir;
        } catch (e) {
          vehicleDirectionCache.current[id] =
            vehicleDirectionCache.current[id] || "unknown";
        }
      }
    };

    refresh();
    directionIntervalRef.current = setInterval(
      refresh,
      DIRECTION_REFRESH_INTERVAL,
    );
    return () => clearInterval(directionIntervalRef.current);
  }, [deviceIds, routesWithVehicles]);

  // -----------------------------------------------------------------
  // --------------------- HAVERSINE HELPER -------------------------
  // -----------------------------------------------------------------
  const haversine = useCallback((lat1, lon1, lat2, lon2) => {
    const toRad = (x) => (x * Math.PI) / 180;
    const R = 6371000;
    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);
    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }, []);

  // -----------------------------------------------------------------
  // --------------------- CALCULATE REALISTIC ETA ------------------
  // -----------------------------------------------------------------
  const calculateRealisticETA = useCallback(
    (distanceKm, deviceId, live) => {
      if (!distanceKm || distanceKm <= 0) {
        return { smoothedETA: 0, etaSeconds: 0 };
      }

      const MAX_ETA_SECONDS = 35 * 60;
      const MAX_ETA_MINUTES = 35;

      const history = vehicleSpeedHistory.current[deviceId] || [];
      let effectiveSpeed = ETA_CONSTANTS.AVG_SPEED;

      let realTimeSpeed = null;
      if (history.length >= 2) {
        const last = history[history.length - 1];
        const prev = history[history.length - 2];
        const timeDiff = (last.timestamp - prev.timestamp) / 500;
        if (timeDiff >= ETA_CONSTANTS.MIN_TIME_DIFF) {
          const dist = haversine(prev.lat, prev.lng, last.lat, last.lng);
          if (dist >= ETA_CONSTANTS.MIN_DISTANCE_THRESHOLD) {
            const speed = dist / 1000 / (timeDiff / 3600);
            if (speed > 0 && speed <= ETA_CONSTANTS.MAX_SPEED_THRESHOLD) {
              realTimeSpeed = speed;
            }
          }
        }
      }

      if (
        !realTimeSpeed &&
        live?.speedKph &&
        live.speedKph > 5 &&
        live.speedKph <= ETA_CONSTANTS.MAX_SPEED_THRESHOLD
      ) {
        realTimeSpeed = live.speedKph;
      }

      const historicalSpeed =
        historicalAvgSpeeds.current[deviceId]?.avgSpeed ||
        ETA_CONSTANTS.AVG_SPEED;

      effectiveSpeed = realTimeSpeed
        ? 0.7 * realTimeSpeed + 0.3 * historicalSpeed
        : historicalSpeed;

      let baseTime = (distanceKm / effectiveSpeed) * 3600;
      baseTime *= ETA_CONSTANTS.TRAFFIC_MULTIPLIER;

      const numLights = Math.floor((distanceKm * 1000) / 800);
      baseTime += numLights * 20;

      baseTime = Math.min(baseTime, MAX_ETA_SECONDS);

      let currentETA = Math.round(baseTime / 60);

      const previousETA = previousETAsRef.current[deviceId] || currentETA;
      const smoothedETA = Math.round(
        ETA_CONSTANTS.EMA_WEIGHT * currentETA +
          (1 - ETA_CONSTANTS.EMA_WEIGHT) * previousETA,
      );

      const finalETA = Math.min(smoothedETA, MAX_ETA_MINUTES);

      previousETAsRef.current[deviceId] = currentETA;

      return { smoothedETA: finalETA, etaSeconds: baseTime };
    },
    [haversine],
  );

  // -----------------------------------------------------------------
  // --------------------- UPDATE HISTORICAL SPEEDS -----------------
  // -----------------------------------------------------------------
  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();
      Object.keys(vehicleSpeedHistory.current).forEach((deviceId) => {
        const history = vehicleSpeedHistory.current[deviceId];
        if (!history || history.length < 2) return;

        let totalDist = 0,
          totalTime = 0,
          valid = 0;
        for (let i = 1; i < history.length; i++) {
          const prev = history[i - 1],
            curr = history[i];
          const dt = (curr.timestamp - prev.timestamp) / 1000;
          if (dt < ETA_CONSTANTS.MIN_TIME_DIFF) continue;
          const d = haversine(prev.lat, prev.lng, curr.lat, curr.lng);
          if (d >= ETA_CONSTANTS.MIN_DISTANCE_THRESHOLD) {
            totalDist += d / 1000;
            totalTime += dt / 3600;
            valid++;
          }
        }
        if (valid >= Math.floor(ETA_CONSTANTS.HISTORY_POINTS / 2)) {
          const avg = totalDist / totalTime;
          if (avg >= 1 && avg <= ETA_CONSTANTS.MAX_SPEED_THRESHOLD) {
            historicalAvgSpeeds.current[deviceId] = {
              avgSpeed: avg,
              lastUpdated: now,
            };
          }
        }
      });
    }, ETA_CONSTANTS.SPEED_UPDATE_INTERVAL);

    return () => clearInterval(interval);
  }, [haversine]);

  // -----------------------------------------------------------------
  // --------- CHECK IF VEHICLE IS APPROACHING (ROUND-TRIP) ---------
  // -----------------------------------------------------------------
  const isVehicleApproaching = useCallback(
    (vehicleDirection, journeyDirection, vehicleNearestIdx, originCoordIdx) => {
      if (
        vehicleDirection === "unknown" ||
        vehicleDirection !== journeyDirection
      ) {
        return false;
      }

      if (journeyDirection === "source_to_dest") {
        return vehicleNearestIdx <= originCoordIdx;
      } else {
        return vehicleNearestIdx >= originCoordIdx;
      }
    },
    [],
  );

  const isVehicleAtChargingStation = useCallback((live) => {
    if (!live?.latitude || !live?.longitude) return false;

    const distanceKm = haversineKm(
      { lat: live.latitude, lng: live.longitude },
      CHARGING_STATION_COORDS,
    );

    return (
      distanceKm !== null && distanceKm * 1000 <= CHARGING_STATION_RADIUS_METERS
    );
  }, []);

  const collectUpcomingBuses = useCallback(
    (locationMap) => {
      if (!routesWithVehicles.length || !currentOrigin || !currentDestination) {
        return [];
      }

      const list = [];
      const seen = new Set();

      for (const route of routesWithVehicles) {
        const journeyDir = getJourneyDirectionForRoute(
          route,
          currentOrigin,
          currentDestination,
        );
        if (!journeyDir) continue;

        const active = (route.vehicles || []).filter(
          (v) => v.status === "ACTIVE",
        );
        const routeCoords = route.routeCoordinates || [];
        const originCoordIdx = findStopIndexOnRoute(routeCoords, currentOrigin);
        if (originCoordIdx === -1) continue;

        for (const v of active) {
          const deviceId =
            v.deviceId || v.device_id || (v.device && v.device.id) || v.id;
          if (!deviceId || seen.has(String(deviceId))) continue;

          const live = locationMap[deviceId];
          if (!live?.latitude || !live?.longitude) continue;
          if (isVehicleAtChargingStation(live)) continue;

          const declaredDirection =
            vehicleDirectionCache.current[deviceId] || "unknown";
          const dir = getEffectiveVehicleDirection(
            declaredDirection,
            vehicleSpeedHistory.current[deviceId],
            routeCoords,
          );

          const nearestIdx = nearestIndex(
            { lat: live.latitude, lng: live.longitude },
            routeCoords,
          );
          const approaching = isVehicleApproaching(
            dir,
            journeyDir,
            nearestIdx,
            originCoordIdx,
          );
          const straightMeters = getStraightMetersToStop(live, currentOrigin);
          const atStopNow = straightMeters <= ARRIVED_DISTANCE_METERS;

          const state = vehicleStateRef.current[deviceId] || {
            atStop: false,
            departed: false,
          };

          if (atStopNow) {
            state.atStop = true;
            state.departed = false;
            state.leftAt = null;
            if (!state.arrivedAt) state.arrivedAt = Date.now();
          } else if (state.atStop) {
            if (!state.leftAt) state.leftAt = Date.now();
            if (Date.now() - state.leftAt < KEEP_ARRIVED_VISIBLE_MS) {
              state.atStop = true;
              state.departed = false;
            } else {
              state.departed = true;
              state.atStop = false;
              state.arrivedAt = null;
              state.leftAt = null;
            }
          } else {
            state.atStop = false;
            state.departed = false;
            state.arrivedAt = null;
            state.leftAt = null;
          }

          vehicleStateRef.current[deviceId] = state;
          if (state.departed) continue;
          if (!approaching && !atStopNow && !state.atStop) continue;

          const pathKm =
            segmentDistance(routeCoords, nearestIdx, originCoordIdx) || 0;
          const etaKm = Math.max(pathKm, straightMeters / 1000);
          const etaResult = calculateRealisticETA(etaKm, deviceId, live);
          const mins = etaResult.smoothedETA;
          const etaSeconds = Number.isFinite(etaResult.etaSeconds)
            ? etaResult.etaSeconds
            : Infinity;

          seen.add(String(deviceId));

          if (mins === null || isNaN(mins) || mins === Infinity) {
            list.push({
              id: v.VehicleNumber || v.name || `Vehicle-${v.id}`,
              vehicleId: v.id || null,
              vehicleName: v.name || "Unknown",
              vehicleNumber: v.VehicleNumber || v.vehicleNumber || null,
              routeName: route.name,
              time: "--:--",
              mins: "—",
              sortMins: Infinity,
              status: v.status,
              description: v.description,
              direction: dir,
              deviceId,
              live,
              routeId: route.id,
              routeCoords,
              nearestIdx,
              originCoordIdx,
              distanceKm: etaKm,
              atStop: atStopNow,
              journeyDirection: journeyDir,
            });
            continue;
          }

          const showArrived =
            (straightMeters < ARRIVED_DISTANCE_METERS &&
              etaSeconds <= ARRIVED_TIME_SECONDS) ||
            (state.atStop && !atStopNow);
          const showArriving =
            straightMeters < ARRIVING_DISTANCE_METERS && !showArrived;

          let displayMins;
          let timeStr;
          let sortMins;
          if (showArrived) {
            displayMins = "Arrived";
            timeStr = "At Stop";
            sortMins = 0;
          } else if (showArriving) {
            displayMins = "Arriving";
            timeStr = formatArrivalClock(mins);
            sortMins = mins;
          } else {
            displayMins = mins <= 0 ? "Arriving" : `${mins} MINS`;
            timeStr = formatArrivalClock(mins);
            sortMins = mins;
          }

          list.push({
            id: v.VehicleNumber || v.name || `Vehicle-${v.id}`,
            vehicleId: v.id || null,
            vehicleName: v.name || "Unknown",
            vehicleNumber: v.VehicleNumber || v.vehicleNumber || null,
            routeName: route.name,
            time: timeStr,
            mins: displayMins,
            sortMins,
            status: v.status,
            description: v.description,
            direction: dir,
            deviceId,
            live,
            routeId: route.id,
            routeCoords,
            nearestIdx,
            originCoordIdx,
            distanceKm: etaKm,
            atStop: atStopNow,
            journeyDirection: journeyDir,
          });
        }
      }

      return list;
    },
    [
      routesWithVehicles,
      currentOrigin,
      currentDestination,
      isVehicleApproaching,
      isVehicleAtChargingStation,
      calculateRealisticETA,
    ],
  );

  const trackableBuses = useMemo(() => {
    if (!currentOrigin || !currentDestination) return [];

    const upcomingById = new Map(
      upcomingBuses.map((bus) => [String(bus.deviceId), bus]),
    );
    const seen = new Set();
    const list = [];

    for (const route of routesWithVehicles) {
      const journeyDir = getJourneyDirectionForRoute(
        route,
        currentOrigin,
        currentDestination,
      );
      if (!journeyDir) continue;

      const routeCoords = route.routeCoordinates || [];
      const originIdx = findStopIndexOnRoute(routeCoords, currentOrigin);
      const destIdx = findStopIndexOnRoute(routeCoords, currentDestination);
      if (originIdx === -1 || destIdx === -1) continue;

      const active = (route.vehicles || []).filter((v) => v.status === "ACTIVE");
      for (const vehicle of active) {
        const deviceId =
          vehicle.deviceId ||
          vehicle.device_id ||
          vehicle.device?.id ||
          vehicle.id;
        if (!deviceId || seen.has(String(deviceId))) continue;

        const upcoming = upcomingById.get(String(deviceId));
        const live = liveData[deviceId] || upcoming?.live;
        if (!live?.latitude || !live?.longitude) continue;
        if (isVehicleAtChargingStation(live)) continue;

        const nearestIdx = nearestIndex(
          { lat: live.latitude, lng: live.longitude },
          routeCoords,
        );
        const declaredDirection =
          vehicleDirectionCache.current[deviceId] ||
          upcoming?.direction ||
          "unknown";
        const direction = getEffectiveVehicleDirection(
          declaredDirection,
          vehicleSpeedHistory.current[deviceId],
          routeCoords,
        );

        if (direction !== journeyDir) continue;

        const onTrip = isVehicleBetweenOriginAndDestination(
          nearestIdx,
          originIdx,
          destIdx,
          journeyDir,
        );

        if (!upcoming && !onTrip) continue;

        seen.add(String(deviceId));
        list.push({
          deviceId,
          vehicleName: upcoming?.vehicleName || vehicle.name || "Unknown",
          routeId: route.id,
          routeCoords,
          journeyDirection: journeyDir,
          live,
          direction,
          onTrip: Boolean(!upcoming && onTrip),
          hint: upcoming ? upcoming.mins : "On trip",
        });
      }
    }

    return list;
  }, [
    routesWithVehicles,
    currentOrigin,
    currentDestination,
    upcomingBuses,
    liveData,
    isVehicleAtChargingStation,
  ]);

  const trackingOptions = useMemo(() => [...trackableBuses], [trackableBuses]);

  const travelOrderedStops = useMemo(
    () => getTravelOrderedStops(routeStops, currentOrigin, currentDestination),
    [routeStops, currentOrigin, currentDestination],
  );

  const stopProgress = useMemo(() => {
    if (!progressVehicle?.deviceId || !travelOrderedStops.length) return null;

    const live = liveData[progressVehicle.deviceId] || progressVehicle.live;
    if (isVehicleAtChargingStation(live)) {
      return lastStopProgressRef.current?.deviceId === progressVehicle.deviceId
        ? { ...lastStopProgressRef.current, stale: true }
        : null;
    }

    const direction =
      vehicleDirectionCache.current[progressVehicle.deviceId] ||
      progressVehicle.journeyDirection;

    let computed = getVehicleStopProgress({
      live,
      routeCoords:
        progressVehicle.routeCoords || selectedRouteForDetails?.routeCoordinates,
      travelStops: travelOrderedStops,
      journeyDirection: progressVehicle.journeyDirection,
      vehicleDirection: direction,
    });

    const arrivedBus = upcomingBuses.find(
      (bus) => String(bus.deviceId) === String(progressVehicle.deviceId),
    );
    const arrivedAtOrigin = Boolean(
      arrivedBus &&
        (arrivedBus.atStop ||
          arrivedBus.mins === "Arrived" ||
          arrivedBus.time === "At Stop"),
    );
    if (
      arrivedAtOrigin &&
      (computed?.lastReached ?? -1) < 0 &&
      (computed?.segmentFraction ?? 0) <= 0
    ) {
      computed = {
        ...(computed || {
          nextIndex: travelOrderedStops.length > 1 ? 1 : -1,
          segmentFromIndex: 0,
          segmentFraction: 0,
          travelStops: travelOrderedStops,
        }),
        lastReached: Math.max(computed?.lastReached ?? -1, 0),
        atStopIndex: computed?.atStopIndex >= 0 ? computed.atStopIndex : 0,
      };
    }

    if (!computed) {
      if (
        live?.latitude &&
        direction &&
        direction !== "unknown" &&
        progressVehicle.journeyDirection &&
        direction !== progressVehicle.journeyDirection
      ) {
        lastStopProgressRef.current = null;
        return null;
      }

      return lastStopProgressRef.current?.deviceId === progressVehicle.deviceId
        ? { ...lastStopProgressRef.current, stale: true }
        : null;
    }

    const prev =
      lastStopProgressRef.current?.deviceId === progressVehicle.deviceId
        ? lastStopProgressRef.current
        : null;
    let segmentFromIndex = computed.segmentFromIndex ?? -1;
    let segmentFraction = computed.segmentFraction ?? 0;
    let lastReached = Math.max(prev?.lastReached ?? -1, computed.lastReached ?? -1);

    if (prev && segmentFromIndex !== -1 && segmentFromIndex < (prev.segmentFromIndex ?? -1)) {
      segmentFromIndex = prev.segmentFromIndex ?? -1;
      segmentFraction = prev.segmentFraction ?? 0;
      lastReached = prev.lastReached ?? lastReached;
    } else if (prev && segmentFromIndex === prev.segmentFromIndex) {
      segmentFraction = Math.max(prev.segmentFraction ?? 0, segmentFraction);
    }

    const nextIndex =
      segmentFromIndex >= 0 && segmentFromIndex + 1 < travelOrderedStops.length
        ? segmentFromIndex + 1
        : -1;

    const next = {
      ...computed,
      lastReached,
      nextIndex,
      segmentFromIndex,
      segmentFraction,
      deviceId: progressVehicle.deviceId,
      stale: false,
    };
    lastStopProgressRef.current = next;
    return next;
  }, [
    progressVehicle,
    travelOrderedStops,
    liveData,
    upcomingBuses,
    selectedRouteForDetails,
    isVehicleAtChargingStation,
  ]);

  useEffect(() => {
    const fromList = trackableBuses.find(
      (bus) => bus.deviceId === progressVehicle?.deviceId,
    );

    if (progressVehicle?.deviceId) {
      if (fromList) {
        pinProgressVehicle(fromList);
        return;
      }
    }

    const first =
      trackableBuses.find((bus) =>
        upcomingBuses.some((item) => item.deviceId === bus.deviceId),
      ) || trackableBuses[0];
    if (first) {
      pinProgressVehicle(first);
      return;
    }

    if (progressVehicle?.deviceId) setProgressVehicle(null);
  }, [
    upcomingBuses,
    trackableBuses,
    progressVehicle?.deviceId,
    pinProgressVehicle,
  ]);

  const handleSelectVehicle = useCallback(
    (deviceId) => {
      const bus =
        upcomingBuses.find((item) => item.deviceId === deviceId) ||
        displayedBuses.find((item) => item.deviceId === deviceId) ||
        trackableBuses.find((item) => item.deviceId === deviceId) ||
        (progressVehicle?.deviceId === deviceId ? progressVehicle : null);
      if (bus) pinProgressVehicle(bus);
      if (upcomingBuses.some((item) => item.deviceId === deviceId)) {
        setSelectedBusDeviceId(deviceId);
      } else {
        setSelectedBusDeviceId(null);
      }
    },
    [
      upcomingBuses,
      displayedBuses,
      trackableBuses,
      progressVehicle,
      pinProgressVehicle,
    ],
  );

  const mapVehicles = useMemo(() => {
    const trackedId = progressVehicle?.deviceId;
    const byId = new Map(
      vehiclesOnMap.map((bus) => [String(bus.deviceId), bus]),
    );

    trackableBuses.forEach((bus) => {
      if (byId.has(String(bus.deviceId))) return;
      const live = liveData[bus.deviceId] || bus.live;
      const lat = Number(live?.latitude);
      const lng = Number(live?.longitude);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;

      const reportedHeading = Number(live?.heading);
      const history = vehicleSpeedHistory.current[bus.deviceId] || [];
      const historyHeading =
        history.length >= 2
          ? getHeadingBetweenPoints(
              history[history.length - 2],
              history[history.length - 1],
            )
          : 0;
      const iconVehicle = mapIconVehicles.find(
        (vehicle) => String(vehicle.device_id) === String(bus.deviceId),
      );

      byId.set(String(bus.deviceId), {
        ...bus,
        position: { lat, lng },
        isSelected: bus.deviceId === trackedId,
        heading: Number.isFinite(reportedHeading)
          ? reportedHeading
          : historyHeading,
        direction: bus.direction || bus.journeyDirection || "unknown",
        vehicleColor: iconVehicle?.vehicle_color || "#008C23",
      });
    });

    return [...byId.values()];
  }, [
    vehiclesOnMap,
    trackableBuses,
    liveData,
    progressVehicle,
    mapIconVehicles,
  ]);

  // -----------------------------------------------------------------
  // --------------------- BUILD / REFRESH BUS LIST -----------------
  // -----------------------------------------------------------------
  const publishUpcomingBuses = useCallback((list) => {
    const signature = list
      .map((bus) => `${bus.deviceId}:${bus.mins}:${bus.sortMins}`)
      .join("|");
    if (signature === lastUpcomingSignatureRef.current) return;
    lastUpcomingSignatureRef.current = signature;
    busesRef.current = list;
    setUpcomingBuses(list.slice(0, 10));
  }, []);

  useEffect(() => {
    if (!routesWithVehicles.length || !currentOrigin || !currentDestination) {
      return undefined;
    }

    let cancelled = false;

    const build = async () => {
      let locations = { ...liveDataRef.current };
      if (Object.keys(locations).length < deviceIds.length) {
        try {
          const map = await fetchInitialLocations(deviceIds);
          locations = { ...locations, ...(map || {}) };
          liveDataRef.current = { ...liveDataRef.current, ...locations };
        } catch (err) {
          console.error("Error fetching initial locations for build:", err);
        }
      }

      for (const route of routesWithVehicles) {
        const active = (route.vehicles || []).filter(
          (v) => v.status === "ACTIVE",
        );
        for (const v of active) {
          const deviceId =
            v.deviceId || v.device_id || (v.device && v.device.id) || v.id;
          if (!deviceId || vehicleDirectionCache.current[deviceId]) continue;
          try {
            vehicleDirectionCache.current[deviceId] =
              await getVehicleDirection(deviceId);
          } catch {
            vehicleDirectionCache.current[deviceId] = "unknown";
          }
        }
      }

      if (cancelled) return;
      publishUpcomingBuses(collectUpcomingBuses(locations));
      setLoadingBuses(false);
    };

    build();
    return () => {
      cancelled = true;
    };
  }, [
    routesWithVehicles,
    currentOrigin,
    currentDestination,
    deviceIds,
    collectUpcomingBuses,
    publishUpcomingBuses,
  ]);

  useEffect(() => {
    if (!currentOrigin || !currentDestination) return;
    publishUpcomingBuses(
      collectUpcomingBuses({ ...liveDataRef.current, ...liveData }),
    );
  }, [
    liveData,
    currentOrigin,
    currentDestination,
    collectUpcomingBuses,
    publishUpcomingBuses,
  ]);

  const handleBackClick = () => navigate(-1);

  const renderStopList = (stops, compact = false) => {
    if (!stops.length) return null;

    return (
      <Box
        sx={
          compact
            ? { maxHeight: 220, overflowY: "auto", mt: 2, pr: 0.5 }
            : undefined
        }
      >
        {stops.map((stop, i) => {
          const progressState = getStopProgressState(stop, stopProgress);
          const isFilled =
            progressState === "passed" || progressState === "current";
          const isCurrent = progressState === "current";
          const iconColor = isCurrent
            ? "#20A8E0"
            : isFilled
              ? "#20A8E0"
              : "#666";
          const nextProgress =
            i < stops.length - 1
              ? getStopProgressState(stops[i + 1], stopProgress)
              : "upcoming";
          const lineFill =
            nextProgress === "passed" || nextProgress === "current"
              ? { fraction: 1, fromBottom: false }
              : i < stops.length - 1
                ? getConnectorFill(stop, stops[i + 1], stopProgress)
                : { fraction: 0, fromBottom: false };

          return (
          <Box
            key={`${stop.id || stop.name}-${i}`}
            sx={{ display: "flex", gap: 2, position: "relative" }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                position: "relative",
              }}
            >
              {isFilled ? (
                <RadioButtonCheckedIcon
                  className={`stop-icon${isCurrent ? " current" : " filled"}`}
                  sx={{
                    fontSize: "20px",
                    width: "20px",
                    height: "20px",
                    color: iconColor,
                    zIndex: 1,
                    flexShrink: 0,
                    background: "#ffffff",
                  }}
                />
              ) : (
                <RadioButtonUncheckedIcon
                  className="stop-icon"
                  sx={{
                    fontSize: "20px",
                    width: "20px",
                    height: "20px",
                    color: iconColor,
                    zIndex: 1,
                    flexShrink: 0,
                    background: "#ffffff",
                  }}
                />
              )}
              {i < stops.length - 1 && (
                <>
                  <Box
                    sx={{
                      position: "absolute",
                      top: 16,
                      left: "50%",
                      transform: "translateX(-50%)",
                      width: "2px",
                      height: "calc(100% - 16px)",
                      bgcolor: "#e0e0e0",
                      zIndex: 0,
                    }}
                  />
                  {lineFill.fraction > 0 && (
                    <Box
                      sx={{
                        position: "absolute",
                        left: "50%",
                        transform: "translateX(-50%)",
                        width: "2px",
                        height: `calc((100% - 16px) * ${lineFill.fraction})`,
                        top: lineFill.fromBottom ? "auto" : 16,
                        bottom: lineFill.fromBottom ? 0 : "auto",
                        bgcolor: "#20A8E0",
                        zIndex: 1,
                      }}
                    />
                  )}
                </>
              )}
            </Box>
            <Box sx={{ pb: compact ? 2 : 3, flex: 1 }}>
              <Typography
                sx={{
                  fontSize: compact ? "13px" : "14px",
                  color: "#000",
                  fontWeight: isCurrent ? 600 : 400,
                }}
              >
                {stop.name}{" "}
                {stop.labelParts && stop.labelParts.length > 0 && (
                  <span
                    style={{ marginLeft: 8, fontWeight: 600, fontSize: 12 }}
                  >
                    (
                    {stop.labelParts.map((part, idx) => (
                      <span
                        key={idx}
                        style={{
                          color:
                            part.className === "last" ? "#EE3B2B" : "#008C23",
                          marginRight:
                            idx < stop.labelParts.length - 1 ? 6 : 0,
                        }}
                      >
                        {part.text}
                        {idx < stop.labelParts.length - 1 ? " • " : ""}
                      </span>
                    ))}
                    )
                  </span>
                )}
              </Typography>
            </Box>
          </Box>
          );
        })}
      </Box>
    );
  };

  const handleSOSClick = () => {
    if (isSendingSOS) return;

    setIsSendingSOS(true);
    const orgPhone =
      organization?.contactPhone || organization?.transportPhoneNumber;
    const phoneNumber = orgPhone?.replace(/[^+\d]/g, "") || "+1234567890";

    if (!phoneNumber) {
      console.error("No SOS phone number available.");
      setIsSendingSOS(false);
      return;
    }

    try {
      window.location.href = `tel:${phoneNumber}`;
    } catch (error) {
      console.error("Failed to initiate call:", error);
    } finally {
      setIsSendingSOS(false);
    }

    // Previous SOS alert flow is preserved here for later use:
    // const contextBus = upcomingBuses[0] || busesRef.current[0] || null;
    //
    // if (!resolvedOrganizationId) {
    //   console.error("Unable to send SOS alert because organization details are missing.");
    //   return;
    // }
    //
    // const userName =
    //   currentUser?.name ||
    //   currentUser?.username ||
    //   currentUser?.email ||
    //   "Unknown User";
    // const userPhone =
    //   currentUser?.phoneNumber || currentUser?.phone_number || "";
    // const userEmail = currentUser?.email || "";
    // const liveLatitude = contextBus?.live?.latitude;
    // const liveLongitude = contextBus?.live?.longitude;
    //
    // const bodyLines = [
    //   `Emergency SOS alert triggered by ${userName}. Please contact the passenger immediately.`,
    //   userPhone ? `Phone: ${userPhone}` : null,
    //   userEmail ? `Email: ${userEmail}` : null,
    //   currentOrigin?.name ? `Origin: ${currentOrigin.name}` : null,
    //   currentDestination?.name
    //     ? `Destination: ${currentDestination.name}`
    //     : null,
    //   contextBus?.routeName ? `Route: ${contextBus.routeName}` : null,
    //   contextBus?.vehicleName ? `Vehicle: ${contextBus.vehicleName}` : null,
    //   contextBus?.vehicleNumber
    //     ? `Vehicle Number: ${contextBus.vehicleNumber}`
    //     : null,
    // ].filter(Boolean);
    //
    // const alertPayload = {
    //   subject: "SOS Alert",
    //   body: bodyLines.join("\n"),
    //   alertType: "EMERGENCY",
    //   medium: "APP",
    //   organizationId: resolvedOrganizationId,
    //   routeId: contextBus?.routeId || undefined,
    //   vehicleId: contextBus?.vehicleId || undefined,
    //   alertStatus: "ACTIVE",
    //   statusFlag: "UNREAD",
    //   userName,
    //   routeName: contextBus?.routeName || undefined,
    //   vehicleName:
    //     contextBus?.vehicleName || contextBus?.vehicleNumber || undefined,
    //   userId: currentUser?.id || undefined,
    // };
    //
    // try {
    //   setIsSendingSOS(true);
    //   await axios.post(`${config.baseURL}/alert`, alertPayload, {
    //     withCredentials: true,
    //   });
    //   setSOSSent(true);
    //   setIsSendingSOS(false);
    //   setTimeout(() => setSOSSent(false), 2000);
    // } catch (error) {
    //   console.error("Failed to send SOS alert:", error);
    //   setIsSendingSOS(false);
    // }
  };

  if (isInactiveOrganization(organization)) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          bgcolor: "#f5f5f5",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 3,
        }}
      >
        <Typography
          sx={{ fontSize: "16px", color: "#5b6472", textAlign: "center" }}
        >
          {t(
            "organizationInactiveTrackingMessage",
            "This organization is inactive. Contact the RouteEye admin to activate it and restore.",
          )}
        </Typography>
      </Box>
    );
  }

  if (!routesWithVehicles.length) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          bgcolor: "#f5f5f5",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 3,
        }}
      >
        <Typography
          sx={{ fontSize: "18px", color: "#666", textAlign: "center" }}
        >
          No route data available.
        </Typography>
      </Box>
    );
  }

  return (
    <Box className="bus-list-container">
      {/* HEADER */}
      <Box
        sx={{
          bgcolor: "white",
          p: 2,
          display: "flex",
          alignItems: "center",
          gap: 2,
          boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
          justifyContent: "space-between",
          flexShrink: 0,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <Button
            onClick={handleBackClick}
            sx={{ minWidth: "auto", p: 1, color: "#333" }}
          >
            <img
              src={backArrow}
              alt="Back"
              width={18}
              height={18}
              style={{ transform: "rotate(90deg)", color: "#183059" }}
            />
          </Button>
          <Typography className="header-title">Upcoming Buses</Typography>
        </Box>
        <Box
          sx={{
            zIndex: 1000,
            width: "auto",
            minWidth: "40px",
            padding: "10px",
            borderRadius: "20%",
            background: "linear-gradient(180deg, #f0483e 0%, #d72020 100%)",
            color: "white",
            fontSize: "14px",
            fontWeight: 700,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: isSendingSOS ? "not-allowed" : "pointer",
            border: "none",
            opacity: isSendingSOS ? 0.7 : 1,
          }}
          component="button"
          type="button"
          onClick={handleSOSClick}
          disabled={isSendingSOS}
        >
          {isSendingSOS ? "Sending" : sosSent ? "Sent" : "SOS"}
        </Box>
      </Box>

      {/* ROUTE SELECTION */}
      <Box
        sx={{
          bgcolor: "white",
          p: 2,
          mt: 1,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexShrink: 0,
        }}
      >
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: "14px", color: "#666" }}>
            from{" "}
            <span
              style={{
                fontWeight: 600,
                color: "#000",
                margin: "5px",
                fontSize: "16px",
              }}
            >
              {currentOrigin?.name || "Origin"}
            </span>{" "}
            to{" "}
            <span
              style={{
                fontWeight: 600,
                color: "#000",
                margin: "5px",
                fontSize: "16px",
              }}
            >
              {currentDestination?.name || "Destination"}
            </span>
          </Typography>
        </Box>
        <Button
          sx={{
            textTransform: "none",
            fontWeight: 500,
            fontSize: "16px",
            color: "#20A8E0",
          }}
          onClick={handleBackClick}
          disabled={!currentOrigin || !currentDestination}
        >
          Change
        </Button>
      </Box>

      {/* BUS CARDS */}
      <Box className="bus-cards-section">
        {!loadingBuses && !shouldHideBuses && upcomingBuses.length > 0 && (
          <Box
            className="bus-cards-filter"
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              mb: 1.5,
              flexShrink: 0,
            }}
          >
            <Select
              size="small"
              value={sortMode}
              onChange={(event) => setSortMode(event.target.value)}
              sx={{
                fontSize: "12px",
                minWidth: 136,
                height: 32,
                borderRadius: "6px",
                backgroundColor: "#FFFFFF",
                "& .MuiSelect-select": {
                  py: 0.5,
                  px: 1.25,
                  fontSize: "12px",
                  fontWeight: 500,
                  color: "#183059",
                  lineHeight: 1.3,
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#D0D7DE",
                },
                "& .MuiSelect-icon": {
                  width: "20px",
                  height: "20px",
                  fontSize: "20px",
                },
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    fontSize: "12px",
                    "& .MuiMenuItem-root": {
                      fontSize: "12px",
                      minHeight: 32,
                      py: 0.75,
                    },
                  },
                },
              }}
            >
              <MenuItem value="arrival">Earliest arrival</MenuItem>
              <MenuItem value="travel_asc">Shortest duration</MenuItem>
              <MenuItem value="travel_desc">Longest duration</MenuItem>
            </Select>
          </Box>
        )}
        <Box className="bus-cards">
        {loadingBuses ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress />
          </Box>
        ) : shouldHideBuses ? (
          <Typography textAlign="center" color="text.secondary" py={2}>
            Sometimes, shuttles may not follow the below route, check with the
            drivers to confirm
          </Typography>
        ) : displayedBuses.length === 0 ? (
          <Typography textAlign="center" color="text.secondary" py={2}>
            Usually, a shuttle arrives at this stop every 20 minutes. However,
            we currently do not have any information about the next shuttle.
            Please check again after some time.
          </Typography>
        ) : (
          displayedBuses.map((bus) => (
            <Box
              key={bus.deviceId}
              className="bus-card"
              onClick={() => handleSelectVehicle(bus.deviceId)}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                width: "100%",
                p: 1.5,
                mb: 0,
                bgcolor:
                  selectedBus?.deviceId === bus.deviceId ? "#EAF8FE" : "#FFFFFF",
                border:
                  selectedBus?.deviceId === bus.deviceId
                    ? "2px solid #20A8E0"
                    : "1px solid #B7BFCC",
                boxShadow: "none",
                opacity:
                  liveData[bus.deviceId] || bus.live ? 1 : 0.7,
                cursor: "pointer",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 1,
                  width: "100%",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    minWidth: 0,
                    flex: "0 1 auto",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "16px",
                      fontWeight: 400,
                      color: "#000",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {bus.vehicleName}
                  </Typography>
                  <Box className="travel-time-badge">
                    <Typography className="travel-time-label">
                      Travel time
                    </Typography>
                    <Typography className="travel-time-value">
                      {bus.travelTimeMins != null
                        ? `${bus.travelTimeMins} MINS`
                        : "—"}
                    </Typography>
                  </Box>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    flexShrink: 0,
                    ml: "auto",
                  }}
                >
                 
                  <Box
                    sx={{
                      bgcolor:
                        bus.mins === "Arrived"
                          ? "#4CAF50"
                          : bus.mins === "Arriving"
                            ? "#FF9800"
                            : "#FFC94D",
                      px: 1.5,
                      py: 0.5,
                      borderRadius: "4px",
                      fontSize: "12px",
                      fontWeight: 500,
                      color:
                        bus.mins === "Arrived" || bus.mins === "Arriving"
                          ? "#FFFFFF"
                          : "#111214",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {bus.mins}
                  </Box>
                  <Typography
                    sx={{
                      fontSize: "16px",
                      fontWeight: 500,
                      color: "#000",
                      minWidth: "72px",
                      textAlign: "right",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {bus.time}
                  </Typography>
                </Box>
              </Box>
            </Box>
          ))
        )}
        </Box>
      </Box>

      {/* ROUTE SECTION */}
      <Box
        className="route-section-fill"
        sx={{
          bgcolor: "white",
          m: 2,
          p: 2,
          border: "1px solid #e0e0e0",
          borderRadius: "8px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
            flexShrink: 0,
          }}
        >
          <Typography sx={{ fontSize: "18px", fontWeight: 600 }}>
            Route
          </Typography>
          <Box
            sx={{
              display: "flex",
              gap: 1,
              border: "1px solid #e0e0e0",
              borderRadius: "16px",
              padding: "4px",
            }}
          >
            <Button
              onClick={() => setActiveRouteTab("stops")}
              sx={{
                backgroundColor:
                  activeRouteTab === "stops" ? "#20A8E0" : "transparent",
                textTransform: "none",
                color: activeRouteTab === "stops" ? "white" : "#666",
                px: 2,
                py: 0.5,
                borderRadius: "16px",
                fontSize: "14px",
                "&:hover": {
                  backgroundColor:
                    activeRouteTab === "stops" ? "#1b94c6" : "rgba(24, 48, 89, 0.08)",
                },
              }}
            >
              Stops
            </Button>
            <Button
              onClick={() => setActiveRouteTab("map")}
              sx={{
                backgroundColor:
                  activeRouteTab === "map" ? "#20A8E0" : "transparent",
                textTransform: "none",
                color: activeRouteTab === "map" ? "white" : "#666",
                px: 2,
                py: 0.5,
                borderRadius: "16px",
                fontSize: "14px",
                "&:hover": {
                  backgroundColor:
                    activeRouteTab === "map" ? "#1b94c6" : "rgba(24, 48, 89, 0.08)",
                },
              }}
            >
              Map
            </Button>
          </Box>
        </Box>

        {activeRouteTab === "stops" && trackingOptions.length > 0 && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              mb: 1.5,
            }}
          >
            <Typography
              sx={{ fontSize: "13px", fontWeight: 600, color: "#20A8E0" }}
            >
              Tracking
            </Typography>
            <Select
              size="small"
              value={
                progressVehicle?.deviceId &&
                trackingOptions.some(
                  (bus) => bus.deviceId === progressVehicle.deviceId,
                )
                  ? progressVehicle.deviceId
                  : trackingOptions[0].deviceId
              }
              onChange={(event) => handleSelectVehicle(event.target.value)}
              renderValue={(deviceId) => {
                const bus =
                  trackingOptions.find((item) => item.deviceId === deviceId) ||
                  progressVehicle;
                return bus?.vehicleName || "Bus";
              }}
              sx={{
                fontSize: "13px",
                fontWeight: 600,
                color: "#20A8E0",
                minWidth: 88,
                height: 32,
                "& .MuiSelect-select": {
                  py: 0.5,
                  px: 1,
                  fontSize: "13px",
                  fontWeight: 600,
                  color: "#20A8E0",
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#20A8E0",
                },
                "& .MuiSelect-icon": {
                  color: "#20A8E0",
                },
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    fontSize: "13px",
                    "& .MuiMenuItem-root": {
                      fontSize: "13px",
                      minHeight: 36,
                    },
                  },
                },
              }}
            >
              {trackingOptions.map((bus) => (
                <MenuItem key={bus.deviceId} value={bus.deviceId}>
                  {bus.vehicleName}
                  {bus.hint ? (
                    <Typography
                      component="span"
                      sx={{ ml: 1, fontSize: "12px", color: "#666" }}
                    >
                      {bus.hint}
                    </Typography>
                  ) : null}
                </MenuItem>
              ))}
            </Select>
          </Box>
        )}

        {loadingStops ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
            <CircularProgress size={20} />
          </Box>
        ) : activeRouteTab === "map" ? (
          <MobileRouteMapPanel
            currentOrigin={currentOrigin}
            currentDestination={currentDestination}
            selectedRouteForDetails={selectedRouteForDetails}
            routeMapPath={routeMapPath}
            routeMapCoordinates={routeMapCoordinates}
            routeMapStops={routeMapStops}
            vehiclesOnMap={mapVehicles}
            getVehicleIcon={getVehicleIcon}
            onSelectVehicle={handleSelectVehicle}
            onTravelTime={handleTravelTime}
            travelRoutes={travelRoutes}
          />
        ) : routeStops.length === 0 ? (
          <Typography textAlign="center" color="text.secondary" py={2}>
            No route stops available.
          </Typography>
        ) : (
          <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
            {renderStopList(routeStops)}
          </Box>
        )}
      </Box>

      {/* INFO BANNER */}
      <Box
        className="info-box info-box-pinned"
        sx={{
          m: 2,
          mt: "auto",
          p: 2,
          borderRadius: "8px",
          bgcolor: "#E8F4FA",
          display: "flex",
          alignItems: "center",
          gap: 2,
          flexShrink: 0,
        }}
      >
        <img src={Info_Banner} alt="info banner" />
        <Typography className="info-text">
          {t(
            "Shuttle service halts for few minutes between the trips and shuttle may stop at charging station for few hours",
          )}
        </Typography>
      </Box>


    </Box>
  );
}
